import axios from 'axios';
import _ from 'lodash';
import { toJS, makeAutoObservable, runInAction, reaction } from 'mobx';
import { getUrlParam } from '../utils/url.utils';
import { getInviteExhibitorId, getInvitePersonTypeIds, capacityQuery } from '../utils/exhibitor.utils';
import { conditionConstants } from '../constants/condition.constants';
import { defaultInvoiceFields } from '../constants/invoiceFields.constants';
import { conditionTranslator, conditionNavigator } from '../utils/condition.utils';
import { HotelStore } from '../stores/hotel/HotelStore';
import { validateOrgno } from '../utils/validation.utils';
// Not from RichTextEditor: that would put the whole editor + KaTeX in the entry bundle.
import { stripHtml } from '../utils/html.utils';
import { consentStore } from '../consent/consentStore';
import { setOrigin, getOrigin } from '../api/origin';
import { preloadLinkedResources } from '../utils/linkedResources.utils';

const API_KEY_WEBFONTS = 'AIzaSyB6aQW4uFg8OuFMHaxBmqDPCvzBGe5vuTg';

const patch = !window?.location?.host?.includes('localhost') ? '/cview' : '';

const axiosClient = axios.create({
  baseURL: `${window.location.protocol}//${window.location.host}${patch}/api`,
});

// This client (created above) is the one that carries almost all of the app's actual requests,
// so this is the only place the header gets attached — api/api.ts exports a separate axios
// instance, but nothing in the app imports it, so an interceptor there would never fire.
axiosClient.interceptors.request.use(config => {
  const origin = getOrigin();
  if (origin) {
    config.headers = config.headers ?? {};
    config.headers['X-Jirango-Origin'] = origin;
  }
  return config;
});

export const basedAxios = axiosClient;

const cache = {};

// Tag answers for a visitor without a saved order (orderId 0, or a new registration's -1), kept for the page view:
// those depend only on the text, event, language and inviting exhibitor, not on what the visitor types. A text module
// asks again whenever the orders change (every field the visitor fills in); this answers those from memory. The
// editor's preview (-7300) and saved orders are always asked for.
const anonymousTagAnswers = new Map();
const EDITOR_PREVIEW_ORDER_ID = -7300;

var updateSqlStateTimeout;

class ContentViewStore {
  constructor() {
    // reevaluatingPersonTypes är en ren rekursionsspärr, inget vyn ska reagera på.
    // favoritesRequest är bara det pågående anropet (ett löfte); linkedResourceEvents och resourceTypesLoaded är ren
    // bokföring av vad som redan hämtats – inget vyn ska reagera på.
    makeAutoObservable(this, {
      reevaluatingPersonTypes: false,
      favoritesRequest: false,
      linkedResourceEvents: false,
      resourceTypesLoaded: false,
    });
    reaction(
      () => this.validations.map(v => v.valid),
      () => this.snapshotCurrentOrderValidation()
    );
    // Warn before leaving the page with unsaved abstract edits (typed/changed but not yet
    // saved as a draft or submitted). Cleared on save/submit/register — see abstractDirty.
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', e => {
        if (this.abstractDirty) {
          e.preventDefault();
          // Browsers ignore custom text and show their own generic prompt.
          e.returnValue = '';
          return '';
        }
      });
    }
  }

  // True when the working abstract has edits that haven't been persisted via saveAbstractDraft,
  // submitAbstracts or a completed registration. Drives the beforeunload guard above.
  abstractDirty = false;
  form = null;
  path = '/cms';
  setPath = p => {
    runInAction(() => {
      this.path = p;
    });
  };

  setForm = id => {
    if (!id) return;
    let parsed = id.split('/').filter(x => x !== 'cms' && x !== 'web' && x !== '');
    if (!parsed || parsed.length !== 1) return;
    parsed = parsed[0];
  };

  current = {
    selectedModule: null,
    activeLayout: null,
    activePage: 1,
    versionIndex: 0,
    template: {},
    payMethod: '',
    hasContentView: false,
    scopedLayout: null,
  };

  environment = {
    eventId: null,
    currency: 'SEK',
    name: '',
    lang: 'swe',
    eventCountry: 'SWE',
    defaultCountry: null,
    countryGroup: '1',
  };
  countries = [];
  axiosClient = undefined;

  contentLayout = {};
  contentHeaderLayout = {};
  contentFooterLayout = {};
  modules = [];
  layoutCode = null;

  hasFetched = false;
  fetching = false;
  lastFetch = null;

  questions = [];

  breakouts = [];

  fields = [];

  mainEvent = null;
  printable = false;
  googleFonts = [];
  uploadedFonts = [];
  clientFontsLoaded = false;
  googleFontsLoaded = false;
  fontAppended = false;
  mainEventFullbooked = false;
  selectedActivitiesFullbooked = false;
  mainEventDeadlinePassed = false;

  resourcesLoaded = false;
  resourcesFailed = false;
  linkedResourcesLoaded = new Set();
  linkedResourceEvents = new Map(); // 'event:<id>' -> the linked layout codes that one request covers
  resourceTypesLoaded = new Set(); // 'fields' | 'questions' | 'breakouts' | 'calendarInvites' in the store for this event
  ordersLoaded = false;
  orders = [];
  sqlStateNo = null;
  activePersonNo = 0;
  // Gates whether payment/invoice validations block registration. Must start true (fail-safe):
  // when undefined/false, getFilteredValidations and checkInvoiceValidation skip both the
  // invoice fields and the PaymentOption validation entirely. Only a completed card-method
  // selection may turn it off (the card terminal collects payment after the post).
  validatePaymentFields = true;

  // Favorites
  favorites = [];
  favoritesLoaded = false;
  favoritesRequest = null;

  // Abstract management
  abstracts = [];
  activeAbstractIndex = 0;
  abstractsLoaded = false;
  availableTopics = [];
  topicsLoaded = false;
  // Event-wide affiliation pool (shared across abstracts) for the author module autocomplete.
  affiliationPool = [];
  affiliationsLoaded = false;
  abstractTaggedQuestionIds = [];
  selectedAbstractIds = [];
  // Pending "submit abstract?" confirmation: { resolve } while the warning dialog is open.
  abstractSubmitConfirm = null;
  notAuthorized = false;
  showPwdDialog = false;
  showPersonPasswordGate = false;
  showEmailLinkGate = false;

  event = {};
  breakoutsFullbooked = {};
  voucherFullbooked = {};

  displayConfirmationSettings = [];
  displayEventSettings = [];
  xrayMode = false;

  userInteracted = false;

  validations = [];
  validate = false;
  orderValidationStates = {}; // { [orderId]: { pages: { [pageNumber]: { invalidFields: [...] } }, hasErrors: boolean, invalidFields: [...aggregated] } }
  openOrderCart = false;

  setOpenOrderCart = (val) => {
    runInAction(() => {
      this.openOrderCart = val;
    });
  };

  voucherList = [];

  conditions = {};

  reevaluatingPersonTypes = false;

  showPageClosed = false;
  
  redirectingToClosedUrl = false;

  hotelStore = new HotelStore(this);

  eventLabels = {};
  systemLabels = [];

  showPwdDialog = false;

  eventNotPublic = false;

  publishAt = null;

  secondsUntilPublish = null;

  menuItems = [];

  fetchData = async (layoutCode, prevState) => {
    if (this.fetching) return;
    if (this.hasFetched && this.lastFetch == layoutCode) return;
    this.fetching = true;
    this.eventNotPublic = false;
    this.linkedResourcesLoaded = new Set();
    this.linkedResourceEvents = new Map();

    const previewToken = getUrlParam('preview');
    axiosClient
      .get(`./cms/${layoutCode}/?newSqlStateNo=${prevState}` + (previewToken ? `&preview=${encodeURIComponent(previewToken)}` : ""))
      .then(async ({ data }) => {
        if (data.notPublic) {
          runInAction(() => {
            this.lastFetch = layoutCode;
            this.eventNotPublic = true;
            this.environment.eventId = data.eventId;
            this.environment.lang = getUrlParam('lang') ?? data.defaultLanguage ?? 'en';
            this.publishAt = data.publishAt;
            this.secondsUntilPublish = data.secondsUntilPublish;
            this.hasFetched = true;
            setOrigin('ContentLayout', null);
          });
          return;
        }
        if (previewToken && window.history?.replaceState) {
          // Server accepted the token and set the preview cookie - clean the URL so copied links are safe.
          const url = new URL(window.location.href);
          url.searchParams.delete('preview');
          window.history.replaceState({}, document.title, url.toString());
        }
        runInAction(async () => {
          this.lastFetch = layoutCode;

          // Another event's page: the fields/questions/breakouts the store holds are not this event's.
          if (this.environment.eventId !== data.contentLayout.eventId) this.resourceTypesLoaded = new Set();

          this.environment.eventId = data.contentLayout.eventId;
          this.environment.currency = data.currency || 'SEK';

          this.contentLayout = data.contentLayout;
          setOrigin('ContentLayout', this.contentLayout?.contentLayoutId);
          this.contentHeaderLayout = data.contentHeaderLayout;
          this.contentFooterLayout = data.contentFooterLayout;

          // A page reached without a full page load (a menu link) inherits "resources loaded" from the page before.
          // Keep that only if what THIS page's modules need really is in the store - the menu preloads it. If not
          // (the preload failed, or has not finished), show the skeletons until fetchResources below has fetched it:
          // a module must never render with its resources missing.
          if (!this.neededResourceTypes().every(type => this.resourceTypesLoaded.has(type))) {
            this.resourcesLoaded = false;
            this.resourcesFailed = false;
          }
          this.modules = data.contentModules;
          this.environment.eventCountry = data.eventCountry;
          this.environment.countryGroup = data.countryGroup;
          this.sqlStateNo = data.sqlStateNo;
          this.mainEvent = data.mainEvent;
          this.printable = data.contentLayout.printable;
          this.current.activePage = 1;
          if (this.contentLayout.conditions) {
            this.contentLayout.conditions = JSON.parse(this.contentLayout.conditions);
          }

          this.contentLayout.contentLayoutModules = this.contentLayout.contentLayoutModules.map(clm => {
            clm.conditions = JSON.parse(clm.conditions);
            return clm;
          });

          [this.contentHeaderLayout, this.contentFooterLayout].forEach(layout => {
            if (layout?.contentLayoutModules) {
              layout.contentLayoutModules = layout.contentLayoutModules.map(clm => {
                if (typeof clm.conditions === 'string') clm.conditions = JSON.parse(clm.conditions);
                return clm;
              });
            }
          });

          this.current.hasContentView = this.contentLayout.contentLayoutModules.find(clm => clm.contentModule.type == 'CONTENT_VIEW');

          const displayEventSettings = {};
          data.displayEventSettings.forEach(de => {
            displayEventSettings[de.name] = de.value;
          });
          this.displayEventSettings = displayEventSettings;

          const displayConfirmationSettings = {};
          data.displayConfirmationSettings.forEach(de => {
            displayConfirmationSettings[de.name] = de.value;
          });
          this.displayConfirmationSettings = displayConfirmationSettings;

          this.current.template = data.templates.find(t => t.templateId == data.contentLayout.templateId);
          this.hasFetched = true;

          const badgeNo = getUrlParam('badgeno') ?? getUrlParam('clientkey');

          const xrayMode = getUrlParam('xray');

          this.xrayMode = xrayMode;
          this.configureEnvironment(data.contentLayout, data);

          if (badgeNo) {
            this.getAttendee(badgeNo, layoutCode);
          } else {
            if (this.orders.length > 0) {
              this.orders = [];
            }
            this.createOrder();
            this.ordersLoaded = true;
          }

          if (this.contentLayout?.groupLeaderChangeLock) {
            var o = this.orders.find(x => x.badgeNo == badgeNo);
            if (o && o.orderGroupId && o.orderGroupId !== o.orderId) {
              this.notAuthorized = true;
            }
          }

          // Only check for deadline if no badgeNo is provided (new order)
          if (!badgeNo && this.contentLayout?.type === 'FORM') {
            this.checkForMainEventDeadline();
          }

          // Cookie consent decides when (or whether) tracking scripts load. Must run before whenAllowed.
          consentStore.init(data.cookieConsent, this.environment.lang, !!getUrlParam('thumbnail'));
          if (this.contentLayout.googleAnalytics) {
            const gaId = this.contentLayout.googleAnalytics;
            consentStore.whenAllowed(`ga:${gaId}`, 'statistics', () => this.appendGoogleAnalytics(gaId));
          }
          if (this.contentLayout.facebookPixel) {
            const pixelId = this.contentLayout.facebookPixel;
            consentStore.whenAllowed(`pixel:${pixelId}`, 'marketing', () => this.appendFacebookPixel(pixelId));
          }
          if (this.contentLayout.documentTitle) {
            document.title = this.contentLayout.documentTitle;
          } else if (this.mainEvent && this.mainEvent.name) {
            document.title = this.mainEvent.name;
          }
          if (this.contentLayout.faviconUrl) {
            //hardcoded, fix
            this.setPageIcon(this.contentLayout.faviconUrl);
          }

          const isClosedByDateTime =
            (data.contentLayout.openDateTime && +new Date(data.contentLayout.openDateTime) > Date.now()) ||
            (data.contentLayout.closeDateTime && +new Date(data.contentLayout.closeDateTime) < Date.now());

          if (isClosedByDateTime) {
            // handlePageClosed returns true when it redirected or when a change-link user
            // is allowed through; otherwise fall back to the closed page as before.
            if (!this.handlePageClosed()) {
              this.showPageClosed = true;
            }
          }

          if (data.contentLayout.password) {
            let pwdItem = localStorage.getItem(`pwd_${this.contentLayout.contentLayoutId}`);
            if (!pwdItem || atob(pwdItem) != data.contentLayout.password) {
              this.showPwdDialog = true;
            }
          }
          if (data.contentLayout.requirePersonPassword && !badgeNo) {
            this.showPersonPasswordGate = true;
          }
          if (data.contentLayout.onlyAuthorizedUsers && data.contentLayout.requestLinkByEmail && !badgeNo) {
            this.showEmailLinkGate = true;
          }
          this.getCountries();
          this.getEventLanguages();
          const isChangeLink = badgeNo;

          const parallelTasks = [];

          if (data.contentLayout.type == 'FORM') {
            if (!isChangeLink) {
              parallelTasks.push(this.checkMainEventBookingStatus());
            }
            if (this.checkSelectedActivitiesBookingStatus) {
              parallelTasks.push(this.checkSelectedActivitiesBookingStatus());
            }
          } else if (data.contentLayout.type == 'SURVEY') this.checkIsSurveyAnswered(layoutCode, badgeNo);

          if (
            this.checkFontStyles(this.current.template?.formattedCss) ||
            this.checkFontStyles(this.contentLayout?.css) ||
            this.checkFontStyles(this.contentHeaderLayout?.css) ||
            this.checkFontStyles(this.contentFooterLayout?.css)
          ) {
            parallelTasks.push(this.handleFonts(this.current.template, this.contentLayout, this.contentHeaderLayout, this.contentFooterLayout));
          }

          if (parallelTasks.length > 0) {
            await Promise.all(parallelTasks);
          }
          // Phase 2: Lazy-load resources AFTER the above tasks complete.
          // Do NOT await — fire-and-forget so the page renders immediately
          // with skeletons, then modules fill in when resources arrive.
          this.fetchResources(layoutCode);
        });
      })
      .catch(err => {
        console.error(err);
        if (err?.response?.status === 404) {
          window.location = window.location.origin;
        }
      })
      .finally(() => {
        this.fetching = false;
      });
  };

  // The resource types the modules of the current page (main layout + header + footer) need.
  neededResourceTypes = () => {
    const moduleTypes = new Set();
    const collectTypes = (layout) => {
      if (layout?.contentLayoutModules) {
        layout.contentLayoutModules.forEach(clm => {
          if (clm.contentModule?.type) moduleTypes.add(clm.contentModule.type);
        });
      }
    };
    collectTypes(this.contentLayout);
    collectTypes(this.contentHeaderLayout);
    collectTypes(this.contentFooterLayout);

    const neededTypes = [];
    if (moduleTypes.has('FIELD') || moduleTypes.has('CUSTOMFIELD') || moduleTypes.has('ORDER_SUMMARY')) neededTypes.push('fields');
    if (moduleTypes.has('QUESTION') || moduleTypes.has('MATCHMAKING') || moduleTypes.has('ACCREDITATION') || moduleTypes.has('ORDER_SUMMARY')) neededTypes.push('questions');
    if (moduleTypes.has('EVENT') || moduleTypes.has('EVENT_GROUP') || moduleTypes.has('HOTEL_MODULE') || moduleTypes.has('VOUCHER') || moduleTypes.has('ORDER_SUMMARY') || moduleTypes.has('PAYMENT_OPTIONS') || moduleTypes.has('PAYMENT_OPTIONS_TWO') || moduleTypes.has('ACCREDITATION') || moduleTypes.has('EXHIBITOR_INVITE_LIST') || moduleTypes.has('TICKETS') || moduleTypes.has('UNREGISTER')) neededTypes.push('breakouts');
    if (moduleTypes.has('CALENDAR_INVITATION')) neededTypes.push('calendarInvites');
    return neededTypes;
  };

  fetchResources = async (layoutCode, retryCount = 0) => {
    const neededTypes = this.neededResourceTypes();

    if (neededTypes.length === 0) {
      runInAction(() => { this.resourcesLoaded = true; });
      return;
    }

    try {
      const { data } = await axiosClient.get(`./cms/${layoutCode}/resources?types=${neededTypes.join(',')}`);

      runInAction(() => {
        if (data.fields) this.fields = data.fields;
        if (data.questions) this.questions = data.questions;
        if (data.breakouts) this.breakouts = data.breakouts;
        if (data.calendarInvites) this.calendarInvites = data.calendarInvites;
        neededTypes.forEach(type => this.resourceTypesLoaded.add(type));
        this.resourcesLoaded = true;
        this.resourcesFailed = false;
      });
    } catch (err) {
      console.error('Failed to load resources', err);
      if (retryCount < 1) {
        return this.fetchResources(layoutCode, retryCount + 1);
      }
      runInAction(() => {
        this.resourcesLoaded = true;
        this.resourcesFailed = true;
      });
    }
  };

  // eventIdByCode: { layoutCode: eventId } for the linked layouts whose event is known. Resources are the same for
  // every layout of one event, so those are covered by ONE request per event; a layout whose event is not known is
  // asked for on its own.
  ensureResourcesForLinkedLayouts = async (layoutCodes, eventIdByCode = {}) => {
    const mergeById = (existing, incoming, idKey) => {
      if (!Array.isArray(incoming) || incoming.length === 0) return existing;
      if (!Array.isArray(existing) || existing.length === 0) return incoming;
      const seen = new Set(existing.map(e => e?.[idKey]));
      const additions = incoming.filter(i => !seen.has(i?.[idKey]));
      return additions.length === 0 ? existing : [...existing, ...additions];
    };

    await preloadLinkedResources({
      layoutCodes,
      eventIdByCode,
      currentLayoutCode: this.lastFetch,
      loadedCodes: this.linkedResourcesLoaded,
      loadedEvents: this.linkedResourceEvents,
      inAction: runInAction,
      fetchResources: async code => {
        const { data } = await axiosClient.get(`./cms/${code}/resources?types=fields,questions,breakouts,calendarInvites`);
        return data;
      },
      applyResources: (data, group) => {
        // What was fetched for THIS page's event is now in the store, whichever page is shown next.
        if (group?.eventId != null && group.eventId === this.environment?.eventId) {
          ['fields', 'questions', 'breakouts', 'calendarInvites'].forEach(type => this.resourceTypesLoaded.add(type));
        }
        if (data.fields) this.fields = mergeById(this.fields, data.fields, 'fieldId');
        if (data.questions) this.questions = mergeById(this.questions, data.questions, 'questionId');
        if (data.breakouts) this.breakouts = mergeById(this.breakouts, data.breakouts, 'eventId');
        if (data.calendarInvites) {
          this.calendarInvites = Array.isArray(this.calendarInvites)
            ? mergeById(this.calendarInvites, data.calendarInvites, 'calendarInviteId')
            : data.calendarInvites;
        }
      },
      onError: (group, err) => console.error(`Failed to preload resources for linked layout(s) ${group.codes.join(', ')}`, err),
    });
  };

  setUserInteracted = val => {
    runInAction(() => {
      this.userInteracted = val;
    });
  };

  checkFontStyles = css => {
    if (!css) return false;

    const importRegex = /@import url\([\s\S]*?\);/gi;
    const fontFaceRegex = /@font-face {[\s\S]*?}/gi;
    const fontNameRegex = /font-family:["'\s]{0,1}(.*?)["'\s]{0,1};/gi;
    let fontName = null;

    if (css.match(importRegex)?.length > 0 || css.match(fontFaceRegex)?.length > 0) return false;
    // let fontName = fontNameRegex.exec(css)
    const SYSTEM_FONTS = [
      'Arial',
      'Helvetica',
      'Times New Roman',
      'Times',
      'Courier New',
      'Courier',
      'Verdana',
      'Georgia',
      'Comic Sans MS',
      'Trebuchet MS',
      'Arial Black',
      'Impact',
    ];
    while (null != (fontName = fontNameRegex.exec(css))) {
      if (SYSTEM_FONTS.includes(fontName[1])) continue;
      else return true;
    }
    return false;
  };

  handleFonts = async (template, contentLayout, header, footer) => {
    // Skip expensive Google Fonts API catalog fetch if all CSS already has font imports
    const needsPatching = this.checkFontStyles(template?.formattedCss) ||
      this.checkFontStyles(contentLayout?.css) ||
      this.checkFontStyles(header?.css) ||
      this.checkFontStyles(footer?.css);
    if (!needsPatching) {
      return;
    }
    await Promise.all([this.fetchGoogleFonts(), this.fetchClientFonts()]);
    const templateFonts = this.addOrUpdateFont(template.formattedCss);
    if (templateFonts) {
      runInAction(() => {
        template.formattedCss = templateFonts + template.formattedCss;
      });
      axiosClient.put(`./utility/template/${template.templateId}`, templateFonts, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
    const contentFonts = this.addOrUpdateFont(contentLayout.css);
    if (contentFonts) {
      runInAction(() => {
        contentLayout.css = contentFonts + contentLayout.css;
      });
      axiosClient.put(`./utility/contentLayout/${contentLayout.contentLayoutId}`, contentFonts, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
    const headerFonts = this.addOrUpdateFont(header?.css);
    if (headerFonts) {
      runInAction(() => {
        header.css = headerFonts + header.css;
      });
      axiosClient.put(`./utility/contentLayout/${header.contentLayoutId}`, headerFonts, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
    const footerFonts = this.addOrUpdateFont(footer?.css);
    if (footerFonts) {
      runInAction(() => {
        footer.css = footerFonts + footer.css;
      });
      axiosClient.put(`./utility/contentLayout/${footer.contentLayoutId}`, footerFonts, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
  };

  getCountries = () => {
    axiosClient.get(`./country/`).then(({ data }) => {
      runInAction(() => {
        this.countries = data;
      });
    });
  };

  // True only when value is a real CountryId present in the loaded country list.
  // Used to reject free-text values the browser may autofill into the country field.
  isValidCountryId = value => {
    if (value === null || value === undefined || value === '') return false;
    const idNum = Number(value);
    if (!Number.isInteger(idNum)) return false;
    return (this.countries ?? []).some(c => c.countryId === idNum);
  };

  setMenuItems = items => {
    if (items !== '' && items !== undefined && items !== null) {
      runInAction(() => {
        this.menuItems = items;
      });
    }
  };

  setPageIcon = url => {
    let link = document.querySelector('link[rel=icon]');
    if (!link) {
      link = document.createElement('link');
      link.id = 'favicon';
      link.rel = 'icon';
      document.head.append(link);
    }
    link.href = url + '?=' + Math.random();
  };

  getEventLanguages = () => {
    axiosClient.get(`./language/${this.environment.eventId}`).then(res => {
      const eventLanguages = res.data;
      runInAction(() => {
        this.event.languages = eventLanguages;
      });
    });
  };

  getAttendee = async (badgeNo, code) => {
    try {
      const res = await axiosClient.get(`./attendee/${badgeNo}?layoutCode=${code}`);
      runInAction(() => {
        const { data } = res;
        if (Array.isArray(data)) {
          this.orders = data;
          this.ordersLoaded = true;
          this.activePersonNo = 0;
          this.checkConditionsOld();
          this.checkConditions();

          // Abstract-tagged questions are answered per abstract, not on the order: every page needs to know them to keep
          // their answers out (shared and cached, so cheap). The attendee's abstracts are only loaded on pages that show them.
          const contactOrderId = data[0]?.orderId;
          if (contactOrderId && contactOrderId > 0) {
            if (this.pageUsesAbstracts) this.fetchAbstracts(contactOrderId);
            this.fetchAbstractTaggedQuestions();
          }
        } else {
          this.notAuthorized = true;
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.ordersLoaded = true;
    }
  };

  verifyPersonPassword = async (password) => {
    const layoutCode = window.location.pathname.split('/').reverse()[0];
    const res = await axiosClient.post('./attendee/verify-person-password', { password, layoutCode });
    return res.data;
  };

  requestEmailLink = async email => {
    const layoutCode = window.location.pathname.split('/').reverse()[0];
    const res = await axiosClient.post(`./attendee/request-link/${encodeURIComponent(layoutCode)}`, { email });
    return res.data;
  };

  //#region FONTS

  fetchGoogleFonts = async () => {
    if (!this.googleFontsLoaded) {
      try {
        const { data } = await axios.get(`https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity&key=${API_KEY_WEBFONTS}`);
        const googleFonts = _.sortBy(data.items, item => item.family).map(({ family }) => family);
        this.googleFonts = googleFonts;
        this.googleFontsLoaded = true;
        return googleFonts;
      } catch (error) {
        console.error(error);
        this.googleFontsLoaded = false;
        return [];
      }
    }
  };

  fetchClientFonts = async () => {
    function cleanedName(name) {
      return name
        .substring(name.lastIndexOf('/') + 1, name.lastIndexOf('.') + 1)
        .toString()
        .replace(')', '')
        .replace('(', '')
        .replace('.', '')
        .replace('1', '')
        .replace('', '');
    }
    if (!this.clientFontsLoaded) {
      try {
        const { data } = await axiosClient.get(`./files/fonts/${this.environment.eventId}`);
        const uploadedFonts = data.files.map(({ url, name }) => ({
          name: cleanedName(name),
          url,
        }));
        this.uploadedFonts = uploadedFonts;
        this.clientFontsLoaded = true;
        return uploadedFonts;
      } catch (error) {
        console.error(error);
        this.clientFontsLoaded = false;
        return [];
      }
    }
  };

  addOrUpdateFont = css => {
    if (!css) return null;
    const { googleFonts, uploadedFonts } = this;
    const fontFamilies = css.match(/font-family:\s*([^;]+);/g);

    if (fontFamilies) {
      const extractedFonts = fontFamilies.map(fontFamily => fontFamily.match(/font-family:\s*([^;]+);/)[1].trim());

      const googleFontsV1 = ['Source Sans Pro'];

      const categorizedFonts = extractedFonts.reduce((acc, font) => {
        if (googleFonts.find(f => f === font) || googleFontsV1.includes(font)) {
          acc.push({ font, type: 'GOOGLE' });
        } else if (uploadedFonts.find(f => f.name === font)) {
          acc.push({
            font,
            type: 'UPLOADED',
            url: uploadedFonts.find(f => f.name === font).url,
          });
        }
        return acc;
      }, []);

      const fontsString = categorizedFonts.reduce((acc, f) => {
        if (f.type === 'GOOGLE') {
          const ff = this.createImport(f.font).replace('https: //', 'https://');
          if (!css.includes(ff) && !acc.includes(ff)) acc += ff + '\r\n';
        } else {
          const ff = this.createFontFace(f);
          if (!css.includes(ff) && !acc.includes(ff)) acc += ff + '\r\n';
        }
        return acc;
      }, '');

      return fontsString;
    }
    return '';
  };

  createFontFace = ({ url, font }) => {
    return `@font-face { font-family: ${font}; src: url('${url}'); }`;
  };

  createImport = font => {
    return `@import url("https://fonts.googleapis.com/css2?family=${font
      .replace(/\s/g, '+')
      .replace(
        '-',
        '+'
      )}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");`;
  };
  //#endregion FONTS

  appendGoogleAnalytics = googleAnalyticsId => {
    var script1 = document.createElement('script');
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId.replace(' ', '')}`;
    script1.async = true;
    document.head.appendChild(script1);

    var script2 = document.createElement('script');
    script2.text = `window.dataLayer = window.dataLayer || [];
                            function gtag(){dataLayer.push(arguments);}
                            gtag('js', new Date());
                            gtag('config', '${googleAnalyticsId.replace(' ', '')}');`;

    document.head.appendChild(script2);
  };

  appendFonts = fontStyles => {
    // const style = document.createElement('style');
    // style.id = 'fontStyles';
    // style.innerHTML = fontStyles;
    // document.head.appendChild(style);
  };

  appendFacebookPixel = facebookPixelId => {
    var script2 = document.createElement('script');
    script2.text = `
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', '${facebookPixelId.replace(' ', '')}');
  fbq('track', 'PageView');
  `;
    document.head.appendChild(script2);
  };

  checkForMainEventDeadline = async () => {
    try {
      const response = await axiosClient.get(`./event/deadline/${this.mainEvent.eventId}`);
      const passed = response?.data?.passed || false;
      this.mainEventDeadlinePassed = passed;
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Shared redirect handling for a closed page. Used both when the layout is closed by
   * date/time and when the main event / the selected activities are fully booked.
   *
   * Returns true when the caller should NOT show the closed page, i.e. either
   *  - a change-link user is allowed through (allowChangeLinkWhenClosed), or
   *  - we are redirecting to the configured redirectUrl.
   * Returns false when there is no redirect configured, leaving the caller to keep
   * using its own closed-state mechanism.
   */
  handlePageClosed = () => {
    const badgeNo = getUrlParam('badgeno') || getUrlParam('clientkey');
    if (this.contentLayout?.allowChangeLinkWhenClosed && badgeNo) {
      // Change link user — allow access even though the form is closed
      return true;
    }
    if (this.contentLayout?.redirectUrl) {
      runInAction(() => {
        this.redirectingToClosedUrl = true;
      });
      window.location.href = this.contentLayout.redirectUrl;
      return true;
    }
    return false;
  };

  checkMainEventBookingStatus = async () => {
    try {
      const response = await axiosClient.get(`./attendee/status/${this.mainEvent.eventId}?sqlStateNo=${this.sqlStateNo}`);
      const available = response?.data?.available || false;
      runInAction(() => {
        this.mainEventFullbooked = !available;
      });
      if (!available) this.handlePageClosed();
      return available;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  checkSelectedActivitiesBookingStatus = async () => {
    const eventIdsJson = this.contentLayout.closeOnFullbookedEventIds;
    if (!eventIdsJson) return;

    try {
      const eventIds = JSON.parse(eventIdsJson);
      if (!eventIds || eventIds.length === 0) return;

      const results = await Promise.allSettled(
        eventIds.map(id =>
          axiosClient.get(`./attendee/getAvailableEventSlots/${id}${capacityQuery(this.orders[this.activePersonNo], '?')}`)
        )
      );

      // Only consider events that still exist (fulfilled responses, 404 = deleted/ignored)
      const existingResults = results.filter(r => r.status === 'fulfilled');
      if (existingResults.length === 0) return;

      const allFullbooked = existingResults.every(r => {
        const slots = r.value?.data?.availableSlots;
        return slots !== null && slots !== undefined && slots <= 0;
      });

      runInAction(() => {
        this.selectedActivitiesFullbooked = allFullbooked;
      });
      if (allFullbooked) this.handlePageClosed();
    } catch (error) {
      console.error('Failed to check selected activities booking status', error);
    }
  };

  configureEnvironment = (layout, res) => {
    const lang = getUrlParam('lang');
    if (lang) {
      this.environment.lang = lang;
    } else {
      this.environment.lang = res.defaultLanguage;
    }

    if (res?.defaultSelectedCountry) {
      this.environment.defaultCountry = parseInt(res?.defaultSelectedCountry);
    }

    if (layout.type == 'FORM') {
      this.environment.name = 'FORM';
      this.sqlStateNo = res.sqlStateNo;
      this.configureValidation();
      this.checkConditionsOld();
      this.checkConditions();
    } else if (layout.type == 'SURVEY') {
      this.environment.name = 'SURVEY';
      this.sqlStateNo = res.sqlStateNo;
      this.configureValidation();
      this.checkConditionsOld();
      this.checkConditions();
    }
  };

  checkInvoiceValidation = isValid => {
    if (
      this.validations
        .filter(v => v.page == this.current.activePage)
        .find(
          v =>
            v.id.toString().includes('INVOICE_') &&
            this.current.payMethod != 'invoice' &&
            this.current.payMethod != 'dibs' &&
            this.current.payMethod != 'stripe' &&
            this.validatePaymentFields
        )
    ) {
      isValid = this.getFilteredValidations().length === 0;
    } else if (
      this.validations
        .filter(v => v.page === this.current.activePage)
        .find(v => (v.id.toString().includes('INVOICE_') && this.current.payMethod != 'invoice') || !this.validatePaymentFields)
    ) {
      isValid = this.getFilteredValidations().length === 0;
    }
    return isValid;
  };

  getFilteredValidations = (allPages = false) => {
    const currentPageValidations = allPages
      ? this.validations
      : this.validations.filter(v => v.page == this.current.activePage || v.page === 0);
    //if any id of any validations is an invoice field (aka includes INVOICE_), AND if current payMethod isn't invoice nor dibs, AND we're suppossed to validate fields
    if (
      currentPageValidations.find(v => v.id.toString().includes('INVOICE_')) &&
      !['invoice', 'dibs', 'stripe'].includes(this.current.payMethod) &&
      this.validatePaymentFields
    ) {
      //return any current page validation that is invalid, visible, and not invoice
      return currentPageValidations.filter(v => v.valid == false && !this.isHiddenByConditions(v.id) && !v.id.toString().includes('INVOICE_') && !v.hidden);
    }
    //if (any id of the validations is an invoice field (aka includes INVOICE_), AND if current payMethod isn't invoice), OR we're not supposed to validate payment fields
    else if (currentPageValidations.find(v => (v.id.toString().includes('INVOICE_') && this.current.payMethod != 'invoice') || !this.validatePaymentFields)) {
      //return any current page validation that is invalid, visible, and not invoice

      return currentPageValidations.filter(v => {
        if (v.valid === false && !this.isHiddenByConditions(v.id)) {
          if (v.id.toString().includes('INVOICE_') && v.forced) {
            return true;
          } else if (!v.id.toString().includes('INVOICE_') && v.label != 'PaymentOption' && !v.hidden) {
            return true;
          }
        }
      });
    } else {
      return currentPageValidations.filter(v => v.valid == false && !this.isHiddenByConditions(v.id) && !v.hidden);
    }
  };

  configureValidation = () => {
    var oldValidations = _.cloneDeep(this.validations);

    this.validations = [];
    const layout = this.current.scopedLayout ? this.current.scopedLayout : this.contentLayout;
    layout?.contentLayoutModules.map(clm => {
      const validationObject = { page: clm.pages, valid: false, id: clm.contentModule.contentModuleId, reason: '' };

      if (clm.contentModule.type == 'FIELD' || clm.contentModule.type == 'QUESTION') {
        if (
          clm.contentModule.settings.required ||
          (clm.contentModule.settings.fieldType &&
            (clm.contentModule.settings.fieldType.toLowerCase() == 'email' ||
              clm.contentModule.settings.fieldType.toLowerCase() == 'firstname' ||
              clm.contentModule.settings.fieldType.toLowerCase() == 'lastname'))
        ) {
          validationObject.reason = 'required';
          validationObject.tracksRequired = true;
          if (clm.contentModule.settings?.fieldType && clm.contentModule.settings?.type?.toLowerCase() === 'person') {
            validationObject.decorator = clm.contentModule.settings.fieldType.toLowerCase();
          }
        } else {
          validationObject.valid = true;
        }
        this.validations.push(validationObject);
      } else if (clm.contentModule.type == 'EVENT' || clm.contentModule.type == 'EVENT_GROUP') {
        if (clm.contentModule.settings.required && !clm.contentModule.settings.prechecked) {
          validationObject.reason = 'required';
          validationObject.tracksRequired = true;
        } else {
          validationObject.valid = true;
        }
        this.validations.push(validationObject);
      } else if (clm.contentModule.type == 'CONFIRM_MEETING') {
        if (clm.contentModule.settings.required) {
          validationObject.reason = 'required';
          validationObject.decorator = 'confirmmeeting';
          validationObject.tracksRequired = true;
        } else {
          validationObject.valid = true;
        }
        this.validations.push(validationObject);
      }

      if (clm.contentModule.type == 'EXHIBITOR_REGISTRATION') {
        if (clm.contentModule.settings.required) {
          validationObject.reason = 'required';
          validationObject.decorator = 'exhibitorregistration';
          validationObject.tracksRequired = true;
        } else {
          validationObject.valid = true;
        }
        this.validations.push(validationObject);
      }
      if (clm.contentModule.type == 'CUSTOMFIELD') {
        if (clm.contentModule.settings.required) {
          validationObject.reason = 'required';
          // (settings.type || '') — a generic custom field has type '' or undefined; calling
          // .toLowerCase() on undefined here would throw and abort the whole validation build.
          validationObject.decorator = 'customfield' + (clm.contentModule.settings.type || '').toLowerCase();
          validationObject.tracksRequired = true;
        } else {
          validationObject.valid = true;
        }
        this.validations.push(validationObject);
      }
      if (clm.contentModule.type == 'PAYMENT_OPTIONS' && clm.contentModule.settings.paymentMethods.includes('invoice')) {
        defaultInvoiceFields.map(df => {
          let defaultValidity = false;
          switch (df) {
            case 'Email':
            case 'Telephone1':
            case 'Address2':
            case 'GLN':
              defaultValidity = true;
              break;
            default:
              defaultValidity = false;
              break;
          }
          this.validations.push({ page: clm.pages, valid: defaultValidity, id: `INVOICE_${df}`, label: df + 'ValidationMessage', type: df });
        });
        if (clm.contentModule.settings.paymentMethods.length > 1) {
          this.validations.push({
            page: clm.pages,
            valid: true,
            id: clm.contentModule.contentModuleId,
            label: 'PaymentOption',
            message: 'PaymentOptionValidationMessage',
          });
        }
      }

      if (clm.contentModule.type == 'PAYMENT_OPTIONS_TWO') {
        defaultInvoiceFields.map(df => {
          let defaultValidity = false;

          let currentValidity = oldValidations.find(v => v.id == `INVOICE_${df}`);
          if (currentValidity != undefined) {
            currentValidity = currentValidity.valid;
          }
          switch (df) {
            case 'Email':
            case 'Address2':
            case 'GLN':
              defaultValidity = true;
              break;
            default:
              defaultValidity = false;
              break;
          }
          this.validations.push({
            page: clm.pages,
            valid: currentValidity ? currentValidity : defaultValidity,
            id: `INVOICE_${df}`,
            label: df + 'ValidationMessage',
            type: df,
          });
        });
        if (clm.contentModule.settings.paymentMethods.length > 1) {
          // Preserve the previous validity (like the invoice fields above): configureValidation
          // re-runs e.g. when a group member is added, and resetting to valid here would let a
          // payable registration through without any payment selection.
          const oldPaymentOption = oldValidations.find(v => v.label === 'PaymentOption' && v.id == clm.contentModule.contentModuleId);
          this.validations.push({
            page: clm.pages,
            valid: oldPaymentOption ? oldPaymentOption.valid : true,
            id: clm.contentModule.contentModuleId,
            label: 'PaymentOption',
            message: 'PaymentOptionValidationMessage',
          });
        }
      } else if (clm.contentModule.type == 'FILE_UPLOADER') {
        const hasFiles = this.orders?.[this.activePersonNo]?.uploadedFiles?.length > 0;
        const fileCount = this.orders?.[this.activePersonNo]?.uploadedFiles?.length || 0;
        const { required, maxFiles } = clm.contentModule.settings;

        if (maxFiles && fileCount > maxFiles) {
          validationObject.label = 'FileUploaderValidation';
          validationObject.valid = false;
          validationObject.reason = 'maxFiles';
        } else if (required && !hasFiles) {
          validationObject.label = 'FileUploaderValidation';
          validationObject.valid = false;
          validationObject.reason = 'required';
        } else {
          validationObject.valid = true;
        }
        if (required) validationObject.tracksRequired = true;
        this.validations.push(validationObject);
      }
      if (clm.contentModule.type == 'PROFILEIMAGE_UPLOADER') {
        if (clm.contentModule.settings.required) {
          validationObject.reason = 'required';
          validationObject.decorator = 'PROFILEIMAGE_UPLOADER';
          validationObject.tracksRequired = true;
        } else {
          validationObject.valid = true;
        }
        this.validations.push(validationObject);
      }
      if (clm.conditions && typeof clm.conditions == 'string' && clm.conditions != 'null') {
        clm.conditions = JSON.parse(clm.conditions);
      }
      if (!this.conditions[clm.contentLayoutModuleId]) this.conditions[clm.contentLayoutModuleId] = { hidden: false, readonly: false, required: false };
      // this.checkConditionsOld(clm.contentLayoutModuleId);
      // this.checkConditions()
    });

    const hasAllDecorators =
      this.validations.filter(
        v => v?.decorator?.toLowerCase() == 'email' || v?.decorator?.toLowerCase() == 'firstname' || v?.decorator?.toLowerCase() == 'lastname'
      ).length !== 3;

    if (hasAllDecorators && this.environment.name === 'FORM' && !this.orders?.[0]?.badgeNo) {
      const mustIncludeValidationObjects = [
        { decorator: 'email', reason: 'required', id: 0, valid: false, page: 0, tracksRequired: true },
        { decorator: 'firstname', reason: 'required', id: 0, valid: false, page: 0, tracksRequired: true },
        { decorator: 'lastname', reason: 'required', id: 0, valid: false, page: 0, tracksRequired: true },
      ];

      mustIncludeValidationObjects?.forEach(v => {
        if (this.validations?.find(x => x.decorator === v.decorator)) return;
        this.validations.push(v);
      });
    }
  };

  replaceTags = async textContent => {
    if (textContent && /\[.*\]/.test(textContent)) {
      const activeOrder = toJS(this.orders)?.[this.activePersonNo];
      const request = {
        text: textContent,
        orderId: activeOrder?.orderId ?? 0,
        badgeNo: activeOrder?.badgeNo,
        eventId: this.environment.eventId,
        lang: this.environment.lang,
        // Inviting exhibitor (voucher link) so [ExhibitorName] etc. resolve before an order exists
        exhibitorId: getInviteExhibitorId(activeOrder),
      };
      const ask = () => axiosClient.put(`./tag/tagreplace`, request).then(response => response.data.text);
      if (request.orderId > 0 || request.orderId === EDITOR_PREVIEW_ORDER_ID) return ask();

      const key = JSON.stringify([request.eventId, request.lang, request.exhibitorId ?? null, textContent]);
      if (!anonymousTagAnswers.has(key)) {
        // The promise is kept, so text modules asking at the same moment share one request; a failure is not kept.
        anonymousTagAnswers.set(
          key,
          ask().catch(err => {
            anonymousTagAnswers.delete(key);
            throw err;
          })
        );
      }
      return anonymousTagAnswers.get(key);
    } else {
      return textContent;
    }
  };

  fetchSessionPlaces = async ({ places, startDate, endDate }) => {
    if (typeof startDate === 'number') {
      startDate = new Date(startDate)
    }
    if (typeof endDate === 'number') {
      endDate = new Date(endDate)
    }

    var response = await axiosClient.post('./cms/getSessions', {
      eventId: this.environment.eventId,
      places: places ?? [],
      startDate: startDate,
      endDate: endDate,
    });
    if (response.status > 199 && response.status < 299) {
      return response.data
    }
  };

  fetchSessionList = async ({ placeIds, startDate, endDate } = {}) => {
    const params = new URLSearchParams();
    params.set('eventId', this.environment.eventId);
    if (placeIds?.length) params.set('placeIds', placeIds.join(','));
    if (startDate) params.set('startDate', new Date(typeof startDate === 'number' ? startDate : startDate).toISOString());
    if (endDate) params.set('endDate', new Date(typeof endDate === 'number' ? endDate : endDate).toISOString());
    var response = await axiosClient.get(`./session/list?${params}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchSessionDetail = async (sessionId) => {
    var response = await axiosClient.get(`./session/${sessionId}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchExhibitorList = async () => {
    var response = await axiosClient.get(`./exhibitor/partial/${this.environment.eventId}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchExhibitorDetail = async (exhibitorId) => {
    var response = await axiosClient.get(`./exhibitor/detail/${exhibitorId}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchExhibitorSelf = async (badgeNo, contentModuleId) => {
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.get(`./exhibitor/self/${badgeNo}${query}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  saveExhibitorSelf = async (badgeNo, payload, contentModuleId) => {
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.put(`./exhibitor/self/${badgeNo}${query}`, payload);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  uploadExhibitorLogo = async (badgeNo, fileDto) => {
    var response = await axiosClient.post(`./exhibitor/self/${badgeNo}/logo`, fileDto);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  uploadExhibitorMediaImage = async (badgeNo, fileDto) => {
    var response = await axiosClient.post(`./exhibitor/self/${badgeNo}/media-image`, fileDto);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  // Sessions where the exhibitor is the head exhibitor. contentModuleId scopes the
  // per-field editability settings to this exhibitor_edit module instance (server-side).
  fetchHeadExhibitorSessions = async (badgeNo, contentModuleId) => {
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.get(`./exhibitor/self/${badgeNo}/sessions${query}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  saveHeadExhibitorSession = async (badgeNo, sessionId, payload, contentModuleId) => {
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.put(`./exhibitor/self/${badgeNo}/session/${sessionId}${query}`, payload);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  uploadHeadExhibitorSessionImage = async (badgeNo, sessionId, fileDto, contentModuleId) => {
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.post(`./exhibitor/self/${badgeNo}/session/${sessionId}/image${query}`, fileDto);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchSpeakerList = async ({ personTypeIds } = {}) => {
    const params = new URLSearchParams();
    params.set('eventId', this.environment.eventId);
    if (personTypeIds?.length) params.set('personTypeIds', personTypeIds.join(','));
    var response = await axiosClient.get(`./speaker/list?${params}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchSpeakerDetail = async (speakerId) => {
    var response = await axiosClient.get(`./speaker/${speakerId}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchAbstractList = async ({ statuses } = {}) => {
    const params = new URLSearchParams();
    params.set('eventId', this.environment.eventId);
    if (statuses) params.set('statuses', statuses);
    var response = await axiosClient.get(`./abstract/list?${params}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchAbstractDetail = async (abstractId) => {
    var response = await axiosClient.get(`./abstract/${abstractId}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  // ── Abstract review (reviewer changelink) ──────────────────

  fetchReviewAssignments = async (badgeNo, contentModuleId) => {
    // contentModuleId scopes blind-review enforcement to the specific module instance (server-side).
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.get(`./review/${this.environment.eventId}/${badgeNo}${query}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchReviewAssignmentDetail = async (badgeNo, abstractId, contentModuleId) => {
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.get(`./review/${this.environment.eventId}/${badgeNo}/${abstractId}${query}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  fetchReviewQuestions = async () => {
    var response = await axiosClient.get(`./review/questions/${this.environment.eventId}?languageCode=${this.environment.lang}`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  saveReview = async (badgeNo, abstractId, answers, complete, contentModuleId) => {
    const query = contentModuleId ? `?contentModuleId=${contentModuleId}` : '';
    var response = await axiosClient.post(`./review/${this.environment.eventId}/${badgeNo}/${abstractId}${query}`, { answers, complete });
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  startReview = async (badgeNo, abstractId) => {
    var response = await axiosClient.post(`./review/${this.environment.eventId}/${badgeNo}/${abstractId}/start`);
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
  };

  // ── Favorites ──────────────────────────────────────────────
  // The server only reads and changes the favourites of the order whose badge is sent along:
  // the badge from the personal link (badgeno / clientkey) is the proof of who is asking.

  favoriteOwner = () => {
    const order = this.orders?.[this.activePersonNo];
    if (!order?.orderId || !order?.badgeNo || !this.environment?.eventId) return null;
    return { orderId: order.orderId, badgeNo: order.badgeNo };
  };

  fetchFavorites = async () => {
    if (this.favoritesLoaded) return this.favorites;
    const owner = this.favoriteOwner();
    if (!owner) return [];

    // Every FavoriteButton on the page asks on mount, before the first answer is back: they all share one request.
    if (this.favoritesRequest) return this.favoritesRequest;
    this.favoritesRequest = (async () => {
      try {
        var response = await axiosClient
          .get('./favorite', {
            params: { eventId: this.environment.eventId, orderId: owner.orderId, badgeNo: owner.badgeNo },
          })
          .catch(error => error.response ?? { status: 0 });
        if (response.status > 199 && response.status < 299) {
          runInAction(() => {
            this.favorites = response.data;
            this.favoritesLoaded = true;
          });
          return response.data;
        }
        return [];
      } finally {
        this.favoritesRequest = null;
      }
    })();
    return this.favoritesRequest;
  };

  addFavorite = async (entityType, entityId) => {
    const owner = this.favoriteOwner();
    if (!owner) return null;
    const { orderId } = owner;

    // Optimistic update
    const tempFavorite = {
      favoriteId: -Date.now(),
      eventId: this.environment.eventId,
      orderId,
      entityType,
      entityId,
      inserted: new Date().toISOString(),
    };
    runInAction(() => {
      this.favorites = [...this.favorites, tempFavorite];
    });

    var response = await axiosClient
      .post('./favorite', {
        eventId: this.environment.eventId,
        badgeNo: owner.badgeNo,
        orderId,
        entityType,
        entityId,
      })
      .catch(error => error.response ?? { status: 0 });
    if (response.status > 199 && response.status < 299) {
      runInAction(() => {
        // Replace temp with real
        this.favorites = this.favorites.map(f =>
          f.favoriteId === tempFavorite.favoriteId
            ? { ...tempFavorite, favoriteId: response.data.favoriteId }
            : f
        );
      });
      return response.data;
    } else {
      // Rollback
      runInAction(() => {
        this.favorites = this.favorites.filter(f => f.favoriteId !== tempFavorite.favoriteId);
      });
      return null;
    }
  };

  removeFavorite = async (favoriteId) => {
    const owner = this.favoriteOwner();
    if (!owner) return;
    const removed = this.favorites.find(f => f.favoriteId === favoriteId);
    // Optimistic removal
    runInAction(() => {
      this.favorites = this.favorites.filter(f => f.favoriteId !== favoriteId);
    });

    var response = await axiosClient
      .delete(`./favorite/${favoriteId}`, { params: { badgeNo: owner.badgeNo } })
      .catch(error => error.response ?? { status: 0 });
    if (!(response.status > 199 && response.status < 299) && removed) {
      // Rollback
      runInAction(() => {
        this.favorites = [...this.favorites, removed];
      });
    }
  };

  fetchFavoriteDetails = async () => {
    const owner = this.favoriteOwner();
    if (!owner) return [];

    var response = await axiosClient
      .get('./favorite/details', {
        params: { eventId: this.environment.eventId, orderId: owner.orderId, badgeNo: owner.badgeNo },
      })
      .catch(error => error.response ?? { status: 0 });
    if (response.status > 199 && response.status < 299) {
      return response.data;
    }
    return [];
  };

  isHiddenByConditions = id => {
    var hidden = false;
    let clm = this.contentLayout.contentLayoutModules.find(x => x.contentModuleId == id);
    if (clm) {
      let isHidden = this.conditions[clm.contentLayoutModuleId]?.hidden;
      if (isHidden) {
        hidden = true;
      }
    }
    return hidden;
  };

  setVoucherList = list => {
    runInAction(() => {
      this.voucherList = list;
    });
  };

  setValidatePaymentFields = val => {
    runInAction(() => {
      this.validatePaymentFields = val;
    });
  };

  checkIsSurveyAnswered = async (layoutCode, badgeNo) => {
    if (layoutCode && badgeNo) {
      const { data } = await axiosClient.get(`./attendee/survey/${layoutCode}/${badgeNo}`);
      const { isAnswered, redirectCode } = data;
      if (isAnswered && redirectCode) {
        window.location.href = `${window.location.origin}${!window.location.href.includes('localhost') ? '/cms' : ''}/web/${redirectCode}${
          window.location.search
        }`;
      }
    }
  };

  checkEventAvailability = async (eventId, orderId, sqlStateNo, add = false) => {
    const count = (this.orders.filter(x => x.orderRow?.some(y => y.eventId === eventId))?.length ?? 0) + (add ? 1 : 0);
    const order = this.orders.find(o => orderId && o.orderId === orderId) ?? this.orders[this.activePersonNo];
    const { data } = await axiosClient.get(
      `./attendee/status/${eventId}${orderId && orderId > 0 ? '/' + orderId : ''}?sqlStateNo=${sqlStateNo ? sqlStateNo : 0}&count=${count}${capacityQuery(order)}`
    );
    return data.available;
  };

  // Cache of which events an exhibitor's vouchers cover, keyed by exhibitorId + personTypes.
  // Powers the "hide event without voucher" module setting. Value is 'loading' while in flight,
  // an array of eventIds once resolved, or undefined if never requested.
  exhibitorVoucherEvents = {};

  voucherEventsKey = (exhibitorId, personTypeIds = []) => `${exhibitorId}|${[...personTypeIds].sort((a, b) => a - b).join(',')}`;

  loadExhibitorVoucherEvents = (exhibitorId, personTypeIds = []) => {
    if (!exhibitorId) return;
    const key = this.voucherEventsKey(exhibitorId, personTypeIds);
    if (this.exhibitorVoucherEvents[key] !== undefined) return; // already loading or loaded
    runInAction(() => {
      this.exhibitorVoucherEvents = { ...this.exhibitorVoucherEvents, [key]: 'loading' };
    });
    axiosClient
      .get(`./exhibitor/voucherEvents?exi=${exhibitorId}&ptis=${personTypeIds.join(',')}`)
      .then(({ data }) => {
        runInAction(() => {
          this.exhibitorVoucherEvents = { ...this.exhibitorVoucherEvents, [key]: data.eventIds || [] };
        });
      })
      .catch(err => {
        runInAction(() => {
          const next = { ...this.exhibitorVoucherEvents };
          delete next[key]; // allow a retry on next render
          this.exhibitorVoucherEvents = next;
        });
        console.error(err);
      });
  };

  fetchAvailableEventSlots = async eventId => {
    try {
      const { data } = await axiosClient.get(`./attendee/getAvailableEventSlots/${eventId}${capacityQuery(this.orders[this.activePersonNo], '?')}`);
      return data.availableSlots;
    } catch (error) {
      console.error('Failed to fetch available slots', error);
      return null;
    }
  };

  updateSqlState = () => {
    let storageStates = localStorage.getItem('formstates');
    let formStates = undefined;

    if (storageStates) {
      formStates = JSON.parse(storageStates);
    } else {
      formStates = [];
    }
    var state = formStates.find(fs => fs.formId === this.contentLayout.contentLayoutId && fs.expires > Date.now());
    if (state) {
      state.expires = Date.now() + 1800000;
    } else {
      formStates.push({
        formId: this.contentLayout.contentLayoutId,
        sqlStateNo: this.sqlStateNo,
        expires: Date.now() + 1800000,
      });
    }

    formStates = formStates.filter(fs => fs.expires > Date.now());
    localStorage.setItem('formstates', JSON.stringify(formStates));

    const body = {
      eventId: this.environment.eventId,
      sqlStateNo: this.sqlStateNo,
      // A plain orders list: the server reads OrderData as a list for activity, hotel and discount capacity.
      stateString: JSON.stringify(this.orders),
    };

    axiosClient.post(`./attendee/`, body, { responseType: 'json' });
  };

  // Abstract management methods
  get currentAbstract() {
    return this.abstracts[this.activeAbstractIndex] || null;
  }

  get hasValidOrder() {
    return this.orders.length > 0 && this.orders[0]?.orderId > 0;
  }

  get isCurrentAbstractEditable() {
    const status = this.currentAbstract?.status;
    return status === 'Draft' || status === 'RevisionRequired';
  }

  // True when any required abstract module (Title, Text part, or Authors) on the current
  // layout is empty for the current abstract. Used to block register; each module also
  // highlights itself inline (driven by the `validate` flag).
  hasAbstractValidationErrors = () => {
    const layout = this.current?.scopedLayout || this.contentLayout;
    const abs = this.currentAbstract;
    if (!layout || !abs) return false;

    const isEmpty = html => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length === 0;
    // Mirror the AbstractText module's word counter so over-limit blocks registration.
    const countWords = html => {
      const t = stripHtml(html || '');
      return t ? t.split(/\s+/).length : 0;
    };

    for (const clm of layout.contentLayoutModules || []) {
      const type = clm.contentModule?.type;
      const s = clm.contentModule?.settings || {};
      if (type === 'ABSTRACT_TITLE') {
        if (s.required && (abs.title || '').trim().length === 0) return true;
      } else if (type === 'ABSTRACT_TEXT') {
        // Defaults mirror the module: parts default visible; Method defaults required, others optional.
        // Each visible part is also invalid when its word count exceeds the part's limit.
        if (s.showIntroduction !== false) {
          if (s.introductionRequired === true && isEmpty(abs.introductionText)) return true;
          if (countWords(abs.introductionText) > (s.introductionMaxWords ?? 150)) return true;
        }
        if (s.showAbstractText !== false) {
          if (s.required !== false && isEmpty(abs.abstractText)) return true;
          if (countWords(abs.abstractText) > (s.maxWords ?? 300)) return true;
        }
        if (s.showResult !== false) {
          if (s.resultRequired === true && isEmpty(abs.resultText)) return true;
          if (countWords(abs.resultText) > (s.resultMaxWords ?? 150)) return true;
        }
        if (s.showConclusion !== false) {
          if (s.conclusionRequired === true && isEmpty(abs.conclusionText)) return true;
          if (countWords(abs.conclusionText) > (s.conclusionMaxWords ?? 150)) return true;
        }
      } else if (type === 'ABSTRACT_AUTHORS') {
        if (s.required && !(abs.authors || []).some(a => (a.email || '').trim().length > 0)) return true;
      } else if (type === 'ABSTRACT_TOPICS') {
        // 0 = no limit. Mirror the module: too few / too many selected topics blocks register.
        const count = (abs.topics || []).length;
        const minTopics = s.minTopics ?? 0;
        const maxTopics = s.maxTopics ?? 0;
        if (minTopics > 0 && count < minTopics) return true;
        if (maxTopics > 0 && count > maxTopics) return true;
      } else if (type === 'PRESENTATION_TYPE') {
        if (s.required !== false && (abs.presentationType || '').trim().length === 0) return true;
      } else if (type === 'FILE_UPLOADER' && s.abstract) {
        // Image caption over-limit blocks register (mirrors the module's caption counter).
        const captionsEnabled = s.captionsEnabled !== false;
        const maxCaptionLength = s.maxCaptionLength ?? 0;
        if (captionsEnabled && maxCaptionLength > 0) {
          const isImage = name => {
            const ext = (name || '').substring((name || '').lastIndexOf('.') + 1).toLowerCase();
            return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'].includes(ext);
          };
          if ((abs.uploadedFiles || []).some(f => isImage(f.name) && (f.caption || '').length > maxCaptionLength)) return true;
        }
      }
    }
    return false;
  };

  // Parse a SaveAbstractResponse from the API into the local abstract format
  _parseAbstractResponse = (r) => ({
    ...r.abstract,
    // r.abstract.affiliations (the union list) is spread in above via ...r.abstract
    authors: (r.authors || []).map(a => ({
      firstName: a.firstName || '',
      lastName: a.lastName || '',
      email: a.email || '',
      organization: a.organization || '',
      isPresenting: a.presenting || false,
      affiliations: a.affiliations || [],
    })),
    keywords: (r.keywords || []).map(k => k.keyword || k),
    topics: r.topicIds || [],
    abstractAnswers: (r.abstractAnswers || []).map(aa => ({
      abstractAnswerId: aa.abstractAnswerId,
      answerId: aa.answerId,
      questionId: aa.questionId,
      questionOptionId: aa.questionOptionId || null,
      text: aa.text || null,
      sortOrder: aa.sortOrder,
    })),
    uploadedFiles: (r.uploadedFiles || r.abstract?.uploadedFiles || [])
      .map(f => ({
        name: f.name || f.filePath?.split('/').pop() || '',
        url: f.url || f.filePath || '',
        fileId: f.fileId,
        size: f.size || 0,
        caption: f.caption || '',
        sortOrder: f.sortOrder ?? 0,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder),
  });

  // Build a SaveAbstractRequest payload from the local abstract format
  _buildAbstractPayload = (abstract) => {
    // Use the abstract's stashed answers (already populated by _stashOrderAnswersToAbstract)
    const abstractAnswers = (abstract.abstractAnswers || []).map(aa => ({
      questionId: aa.questionId,
      questionOptionId: aa.questionOptionId || null,
      text: aa.text || null,
    }));

    return {
      abstractId: abstract.abstractId,
      eventId: abstract.eventId,
      contactOrderId: abstract.contactOrderId,
      presentationType: abstract.presentationType || '',
      title: abstract.title || '',
      introductionText: abstract.introductionText || '',
      abstractText: abstract.abstractText || '',
      resultText: abstract.resultText || '',
      conclusionText: abstract.conclusionText || '',
      status: abstract.status || 'Draft',
      affiliations: abstract.affiliations || [],
      authors: (abstract.authors || []).map(a => ({
        firstName: a.firstName || '',
        lastName: a.lastName || '',
        email: a.email || '',
        organization: a.organization || '',
        presenting: a.isPresenting || false,
        affiliations: a.affiliations || [],
      })),
      keywords: abstract.keywords || [],
      topicIds: abstract.topics || [],
      abstractAnswers,
      uploadedFiles: (abstract.uploadedFiles || []).map((f, i) => ({
        name: f.name,
        url: f.url,
        fileId: f.fileId,
        size: f.size,
        caption: f.caption || '',
        // Persist the current visible order as the sort order (drag-reorder updates this).
        sortOrder: f.sortOrder ?? i,
      })),
    };
  };

  fetchAbstracts = async (contactOrderId) => {
    try {
      const { data } = await axiosClient.get(
        `./abstract/${this.environment.eventId}/${contactOrderId}`
      );
      runInAction(() => {
        this.abstracts = data.map(r => this._parseAbstractResponse(r));
        this.abstractsLoaded = true;
        // Load the active abstract's answers into the order
        if (this.currentAbstract) {
          this._loadAbstractAnswersIntoOrder(this.currentAbstract);
        }
      });
    } catch (error) {
      console.error('Failed to fetch abstracts', error);
      runInAction(() => {
        this.abstractsLoaded = true;
      });
    }
  };

  fetchTopics = async () => {
    try {
      const { data } = await axiosClient.get(`./topic/${this.environment.eventId}`);
      runInAction(() => {
        this.availableTopics = data;
        this.topicsLoaded = true;
      });
    } catch (error) {
      console.error('Failed to fetch topics', error);
      runInAction(() => {
        this.topicsLoaded = true;
      });
    }
  };

  fetchAbstractTaggedQuestions = async () => {
    try {
      const { data } = await axiosClient.get(`./questiontag/${this.environment.eventId}/abstract`);
      runInAction(() => {
        this.abstractTaggedQuestionIds = data || [];
        // A page without abstract modules never shows abstract answers: take them out of the order, so neither the
        // page nor a summary shows them (saving leaves them out as well, see formPost).
        if (!this.pageUsesAbstracts && this.abstractTaggedQuestionIds.length) {
          this.orders.forEach(order => {
            if (order?.answer) order.answer = order.answer.filter(ans => !this.abstractTaggedQuestionIds.includes(ans.questionId));
          });
        }
      });
    } catch (error) {
      console.error('Failed to fetch abstract-tagged questions', error);
    }
  };

  toggleAbstractSelection = (id) => {
    runInAction(() => {
      if (this.selectedAbstractIds.includes(id)) {
        this.selectedAbstractIds = this.selectedAbstractIds.filter(x => x !== id);
      } else {
        this.selectedAbstractIds = [...this.selectedAbstractIds, id];
      }
    });
    this.debouncedUpdateSqlState();
  };

  createAbstract = () => {
    runInAction(() => {
      const newAbstract = {
        abstractId: -(this.abstracts.length + 1),
        eventId: this.environment.eventId,
        contactOrderId: this.orders[0]?.orderId > 0 ? this.orders[0].orderId : 0,
        presentationType: '',
        title: '',
        status: 'Draft',
        authors: [{ firstName: '', lastName: '', email: '', organization: '', isPresenting: false }],
        abstractText: '',
        keywords: [],
        topics: [],
        uploadedFiles: [],
        abstractAnswers: [],
      };
      this.abstracts.push(newAbstract);
      // Stash current abstract's answers before switching
      if (this.currentAbstract) {
        this._stashOrderAnswersToAbstract(this.activeAbstractIndex);
      }
      this.activeAbstractIndex = this.abstracts.length - 1;
      // Load empty answers for the new abstract (clears tagged questions)
      this._loadAbstractAnswersIntoOrder(this.currentAbstract);
    });
    this.debouncedUpdateSqlState();
  };

  addAbstractFile = (file) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const files = [...(this.currentAbstract.uploadedFiles || [])];
        // New file goes to the end; its sortOrder is its position (figure numbering follows).
        files.push({ caption: '', ...file, sortOrder: files.length });
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          uploadedFiles: files,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  removeAbstractFile = (fileUrl) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const files = (this.currentAbstract.uploadedFiles || [])
          .filter(f => f.url !== fileUrl)
          .map((f, i) => ({ ...f, sortOrder: i }));
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          uploadedFiles: files,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  // Update the caption (explanatory figure text) of an uploaded abstract file.
  updateAbstractFileCaption = (fileId, caption) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const files = (this.currentAbstract.uploadedFiles || [])
          .map(f => (f.fileId === fileId ? { ...f, caption } : f));
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          uploadedFiles: files,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  // Reorder uploaded abstract files (drag-and-drop). Moves the file at fromIndex to
  // toIndex and renumbers sortOrder so figure numbers follow the new order.
  reorderAbstractFiles = (fromIndex, toIndex) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const files = [...(this.currentAbstract.uploadedFiles || [])];
        if (fromIndex < 0 || fromIndex >= files.length || toIndex < 0 || toIndex >= files.length) return;
        const [moved] = files.splice(fromIndex, 1);
        files.splice(toIndex, 0, moved);
        const renumbered = files.map((f, i) => ({ ...f, sortOrder: i }));
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          uploadedFiles: renumbered,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  // Stash the current order's abstract-tagged answers into the abstract at the given index
  _stashOrderAnswersToAbstract = (abstractIndex) => {
    if (!this.orders.length || !this.abstractTaggedQuestionIds.length) return;
    const order = this.orders[0];
    if (!order?.answer) return;

    const stashedAnswers = [];
    order.answer.forEach(ans => {
      if (this.abstractTaggedQuestionIds.includes(ans.questionId)) {
        const rows = (ans.answerRow || []).filter(r => !r.deleted);
        rows.forEach(row => {
          stashedAnswers.push({
            questionId: ans.questionId,
            questionOptionId: row.questionOptionId || null,
            text: row.text || null,
          });
        });
      }
    });

    this.abstracts[abstractIndex] = {
      ...this.abstracts[abstractIndex],
      abstractAnswers: stashedAnswers,
    };
  };

  // Load an abstract's answers into the order, replacing any existing tagged-question answers
  _loadAbstractAnswersIntoOrder = (abstract) => {
    if (!this.orders.length || !this.abstractTaggedQuestionIds.length) return;
    const order = this.orders[0];
    if (!order) return;

    if (!order.answer) {
      order.answer = [];
    }

    // Remove existing answers for all abstract-tagged questions
    order.answer = order.answer.filter(ans => !this.abstractTaggedQuestionIds.includes(ans.questionId));

    // Add answers from the abstract. Only abstract-tagged questions: formPost leaves just those out, so any other
    // answer added here (answerId 0) would be saved as a new answer on the order every time.
    const answers = (abstract?.abstractAnswers || []).filter(aa => this.abstractTaggedQuestionIds.includes(aa.questionId));
    // Group by questionId
    const grouped = {};
    answers.forEach(aa => {
      if (!grouped[aa.questionId]) grouped[aa.questionId] = [];
      grouped[aa.questionId].push(aa);
    });

    Object.entries(grouped).forEach(([questionId, rows]) => {
      order.answer.push({
        answerId: 0,
        interactionKey: null,
        orderId: order.orderId || 0,
        questionId: parseInt(questionId),
        answerRow: rows.map(r => ({
          answerId: 0,
          answerRowId: 0,
          questionOptionId: r.questionOptionId || null,
          text: r.text || null,
          deleted: false,
        })),
      });
    });

    this.orders[0] = { ...order };
    this.orders = [...this.orders];
  };

  setActiveAbstract = (index) => {
    runInAction(() => {
      // Stash current abstract's answers before switching
      this._stashOrderAnswersToAbstract(this.activeAbstractIndex);
      this.activeAbstractIndex = index;
      // Load new abstract's answers into the order
      this._loadAbstractAnswersIntoOrder(this.currentAbstract);
    });
  };

  updateAbstractField = (field, value) => {
    runInAction(() => {
      if (this.currentAbstract) {
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          [field]: value,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  addAuthor = () => {
    runInAction(() => {
      if (this.currentAbstract) {
        const authors = [...(this.currentAbstract.authors || [])];
        authors.push({ firstName: '', lastName: '', email: '', organization: '', isPresenting: false, affiliations: [] });
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          authors,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  removeAuthor = (index) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const authors = [...(this.currentAbstract.authors || [])];
        authors.splice(index, 1);
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          authors,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  updateAuthor = (index, field, value) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const authors = [...(this.currentAbstract.authors || [])];
        authors[index] = { ...authors[index], [field]: value };
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          authors,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  // ── Author affiliations ─────────────────────────────────────────────
  // Affiliations are shared event-wide; the "above authors" field is this abstract's
  // working list, and each author links to a subset of it.

  fetchAffiliations = async () => {
    try {
      const { data } = await axiosClient.get(`./abstract/affiliations/${this.environment.eventId}`);
      runInAction(() => {
        this.affiliationPool = (data || []).map(a => (typeof a === 'string' ? a : a.name)).filter(Boolean);
        this.affiliationsLoaded = true;
      });
    } catch (error) {
      console.error('Failed to fetch affiliations', error);
    }
  };

  // Add a name to the abstract's working affiliation list (deduped, case-insensitive).
  addAffiliation = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    runInAction(() => {
      if (!this.currentAbstract) return;
      const list = [...(this.currentAbstract.affiliations || [])];
      if (!list.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
        list.push(trimmed);
        this.abstracts[this.activeAbstractIndex] = { ...this.abstracts[this.activeAbstractIndex], affiliations: list };
        this.abstracts = [...this.abstracts];
      }
      // Make it immediately autocomplete-able in this session too.
      if (!this.affiliationPool.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
        this.affiliationPool = [...this.affiliationPool, trimmed].sort((a, b) => a.localeCompare(b));
      }
    });
    this.debouncedUpdateSqlState();
  };

  // Remove an affiliation from the abstract's list and unlink it from every author.
  removeAffiliation = (name) => {
    runInAction(() => {
      if (!this.currentAbstract) return;
      const lower = (name || '').toLowerCase();
      const affiliations = (this.currentAbstract.affiliations || []).filter(n => n.toLowerCase() !== lower);
      const authors = (this.currentAbstract.authors || []).map(a => ({
        ...a,
        affiliations: (a.affiliations || []).filter(n => n.toLowerCase() !== lower),
      }));
      this.abstracts[this.activeAbstractIndex] = { ...this.abstracts[this.activeAbstractIndex], affiliations, authors };
      this.abstracts = [...this.abstracts];
    });
    this.debouncedUpdateSqlState();
  };

  // Toggle whether an author is linked to a given affiliation. The affiliation stays in the
  // abstract's available list even if no author is linked — unlinked ones are dropped on save
  // (the server only persists affiliations that have at least one author).
  toggleAuthorAffiliation = (authorIndex, name) => {
    runInAction(() => {
      if (!this.currentAbstract) return;
      const lower = name.toLowerCase();
      const authors = [...(this.currentAbstract.authors || [])];
      const current = authors[authorIndex]?.affiliations || [];
      const has = current.some(n => n.toLowerCase() === lower);
      authors[authorIndex] = {
        ...authors[authorIndex],
        affiliations: has ? current.filter(n => n.toLowerCase() !== lower) : [...current, name],
      };
      this.abstracts[this.activeAbstractIndex] = { ...this.abstracts[this.activeAbstractIndex], authors };
      this.abstracts = [...this.abstracts];
    });
    this.debouncedUpdateSqlState();
  };

  addKeyword = (keyword) => {
    runInAction(() => {
      if (this.currentAbstract && keyword.trim()) {
        const keywords = [...(this.currentAbstract.keywords || [])];
        keywords.push(keyword.trim());
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          keywords,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  removeKeyword = (index) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const keywords = [...(this.currentAbstract.keywords || [])];
        keywords.splice(index, 1);
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          keywords,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  toggleTopic = (topicId) => {
    runInAction(() => {
      if (this.currentAbstract) {
        const topics = [...(this.currentAbstract.topics || [])];
        const idx = topics.indexOf(topicId);
        if (idx >= 0) topics.splice(idx, 1);
        else topics.push(topicId);
        this.abstracts[this.activeAbstractIndex] = {
          ...this.abstracts[this.activeAbstractIndex],
          topics,
        };
        this.abstracts = [...this.abstracts];
      }
    });
    this.debouncedUpdateSqlState();
  };

  // The attendee's abstracts are read by the abstract modules (submit, management and the abstract field modules), the
  // presentation-type module and abstract file uploads on the page, its header or its footer. A content view may swap
  // such a page in, so it loads them too. Other pages skip both abstract calls.
  get pageUsesAbstracts() {
    return [this.contentLayout, this.contentHeaderLayout, this.contentFooterLayout].some(layout =>
      layout?.contentLayoutModules?.some(clm => {
        const type = clm.contentModule?.type ?? '';
        return (
          type.startsWith('ABSTRACT_') ||
          type === 'PRESENTATION_TYPE' ||
          type === 'CONTENT_VIEW' ||
          (type === 'FILE_UPLOADER' && !!clm.contentModule?.settings?.abstract)
        );
      })
    );
  }

  get layoutHasAbstractSubmitModule() {
    return !!this.contentLayout?.contentLayoutModules?.some(clm => clm.contentModule?.type === 'ABSTRACT_SUBMIT');
  }

  get layoutHasAbstractManagementModule() {
    return !!this.contentLayout?.contentLayoutModules?.some(clm => clm.contentModule?.type === 'ABSTRACT_MANAGEMENT');
  }

  // Activity the payment module adds as an invoice fee, or null when the feature is off.
  get invoiceFeeEventId() {
    const clm = this.contentLayout?.contentLayoutModules?.find(
      clm =>
        ['PAYMENT_OPTIONS', 'PAYMENT_OPTIONS_TWO'].includes(clm.contentModule?.type) &&
        clm.contentModule?.settings?.invoiceFee &&
        clm.contentModule?.settings?.invoiceFeeEventId
    );
    return clm ? Number(clm.contentModule.settings.invoiceFeeEventId) : null;
  }

  // Opens the submit-warning dialog (rendered by AbstractManagement) and resolves
  // with the user's choice. `abstracts` = [{ abstractId, title }] that will be
  // submitted and locked. mode 'none' = nothing selected, drafts will NOT be submitted.
  requestAbstractSubmitConfirmation = (abstracts = [], mode = 'submit') =>
    new Promise(resolve => {
      runInAction(() => {
        this.abstractSubmitConfirm = { resolve, abstracts, mode };
      });
    });

  resolveAbstractSubmitConfirmation = confirmed => {
    const pending = this.abstractSubmitConfirm;
    runInAction(() => {
      this.abstractSubmitConfirm = null;
    });
    pending?.resolve(!!confirmed);
  };

  lookupAbstractAuthor = async email => {
    if (!email?.trim()) return null;
    try {
      const { data } = await axiosClient.get(`./abstract/author-lookup/${this.environment.eventId}?email=${encodeURIComponent(email.trim())}`);
      return data?.found ? data : null;
    } catch (error) {
      console.error('Failed to look up author', error);
      return null;
    }
  };

  submitAbstracts = async (abstractIds) => {
    // Stash current answers so the active abstract has latest data
    this._stashOrderAnswersToAbstract(this.activeAbstractIndex);
    try {
      for (const id of abstractIds) {
        const idx = this.abstracts.findIndex(a => a.abstractId === id);
        if (idx >= 0) {
          const abstractData = { ...this.abstracts[idx], status: 'Submitted' };
          const payload = this._buildAbstractPayload(abstractData);
          const { data } = abstractData.abstractId < 0
            ? await axiosClient.post('./abstract/', payload)
            : await axiosClient.put('./abstract/', payload);
          runInAction(() => {
            this.abstracts[idx] = this._parseAbstractResponse(data);
            this.abstracts = [...this.abstracts];
          });
        }
      }
      runInAction(() => { this.abstractDirty = false; });
    } catch (error) {
      console.error('Failed to submit abstracts', error);
    }
  };

  debouncedUpdateSqlState = () => {
    // Every abstract working-state mutation funnels through here, so this is also where we
    // flag that there are unsaved abstract edits (used by the beforeunload guard).
    this.abstractDirty = true;
    clearTimeout(updateSqlStateTimeout);
    updateSqlStateTimeout = setTimeout(() => {
      this.updateSqlState();
    }, 500);
  };

  saveAbstractDraft = async () => {
    // Stash latest answers from order into the abstract before building payload
    this._stashOrderAnswersToAbstract(this.activeAbstractIndex);
    const abstractData = this.currentAbstract;
    if (!abstractData) return;

    const payload = this._buildAbstractPayload(abstractData);

    try {
      if (abstractData.abstractId < 0) {
        // New abstract - create
        const { data } = await axiosClient.post('./abstract/', payload);
        runInAction(() => {
          this.abstracts[this.activeAbstractIndex] = this._parseAbstractResponse(data);
          this.abstracts = [...this.abstracts];
        });
      } else {
        // Existing abstract - update
        const { data } = await axiosClient.put('./abstract/', payload);
        runInAction(() => {
          this.abstracts[this.activeAbstractIndex] = this._parseAbstractResponse(data);
          this.abstracts = [...this.abstracts];
        });
      }
      runInAction(() => { this.abstractDirty = false; });
      return true;
    } catch (error) {
      console.error('Failed to save abstract draft', error);
      return false;
    }
  };

  updateOrder = (updatedOrder, personNo = this.activePersonNo) => {
    const nextOrder = {
      ...this.orders[personNo],
      ...updatedOrder,
      person: {
        ...(this.orders[personNo]?.person ?? {}),
        ...(updatedOrder?.person ?? {}),
      },
      orderExtra: [
        ...(this.orders[personNo]?.orderExtra ?? []),
        ...(updatedOrder?.orderExtra?.filter(item => !this.orders?.[personNo]?.orderExtra?.some(x => x.orderExtraId === item.orderExtraId)) ?? []),
      ],
    };

    nextOrder.sqlStateNo = this.sqlStateNo; // fix
    nextOrder.contentLayoutId = this.contentLayout.contentLayoutId;
    nextOrder.eventId = this.environment.eventId;
    nextOrder.languageCode = this.environment.lang;
    const exhibitorId = getUrlParam('exi');

    if (exhibitorId && !isNaN(exhibitorId)) {
      if (!nextOrder.orderExtra)
        nextOrder.orderExtra = [
          {
            orderExtraId: 0,
            name: 'ExhibitorInvite',
            value: `${exhibitorId}`,
            value2: '',
          },
        ];
      else if (!nextOrder.orderExtra.some(oe => oe.name == 'ExhibitorInvite')) {
        nextOrder.orderExtra.push({
          orderExtraId: 0,
          name: 'ExhibitorInvite',
          value: `${exhibitorId}`,
          value2: '',
        });
      } else if (nextOrder.orderExtra.find(oe => oe.name == 'ExhibitorInvite')) {
        nextOrder.orderExtra.find(oe => oe.name == 'ExhibitorInvite').value = `${exhibitorId}`;
      }
    }

    if (!nextOrder.orderRow) nextOrder.orderRow = [];
    if (!nextOrder?.orderRow?.some(row => row.eventId === this.environment.eventId)) {
      nextOrder.orderRow.push({
        amount: this.mainEvent.amount,
        comment: '',
        discount: 0,
        eventId: this.mainEvent.eventId,
        orderId: -1,
        orderRowId: nextOrder.orderRow.length * -1,
        quantity: 1,
        status: null,
        vatPercent: this.mainEvent.vatPercent,
        deleted: false,
      });
    }

    if (updateSqlStateTimeout) {
      clearTimeout(updateSqlStateTimeout);
    }

    updateSqlStateTimeout = setTimeout(this.updateSqlState, 500);
    runInAction(() => {
      this.orders[personNo] = nextOrder;
      this.orders = [...this.orders];
    });
    const personTypeId = getUrlParam('pti');
    if (personTypeId && !isNaN(personTypeId)) {
      this.addPersonType(parseInt(personTypeId));
    }
  };

  createNewOrderRowId() {
    const nonCreatedOrderRows = this.orderRows?.filter(row => row.orderRowId < 0)?.map(row => row.orderRowId) ?? [];

    if (!nonCreatedOrderRows.length) return -1;

    const newOrderRowId = (Math.min(...nonCreatedOrderRows) ?? 0) - 1;
    return newOrderRowId;
  }

  updateOrderRow = (orderRow, forceUncheck = false, prechecked = null, displayYesNo = null) => {
    runInAction(() => {
      if (this.orders?.length === 0) {
        this.createOrder();
      }

      let order = this.orders[this.activePersonNo];

      if (orderRow?.orderId && order.orderId !== orderRow.orderId) {
        order = this.orders.find(o => o.orderId === orderRow.orderId);
      }

      // A row of an order that isn't loaded here, e.g. the booker's hotel row a roommate sees: leave it alone.
      if (!order) {
        console.warn(`updateOrderRow: order ${orderRow?.orderId} is not loaded, row ${orderRow?.orderRowId} left unchanged`);
        return;
      }

      if (!order.orderRow) {
        order.orderRow = [
          {
            amount: this.mainEvent.amount,
            comment: '',
            discount: 0,
            eventId: this.mainEvent.eventId,
            orderId: -1,
            orderRowId: this.createNewOrderRowId(),
            quantity: 1,
            status: null,
            vatPercent: this.mainEvent.vatPercent,
            deleted: false,
          },
        ];
      }

      if (orderRow?.eventId && !order.orderRow.find(or => or.eventId === orderRow?.eventId) && !forceUncheck) {
        order.orderRow.push(orderRow);
      } else if ((forceUncheck || (orderRow.deleted && orderRow?.eventId)) && orderRow.status !== 'Cancelled') {
        order.orderRow = order.orderRow.filter(or => or.eventId !== orderRow.eventId);
      } else if (!orderRow.hotelGroupId && !orderRow.hotelAllotmentPricingId) {
        // A saved row replaces only itself. Matching on the event alone overwrote a second saved row of the same
        // event, which then wasn't posted and was deleted on save.
        const savedRowInOrder = orderRow.orderRowId > 0 && order.orderRow.some(or => or.orderRowId === orderRow.orderRowId);
        order.orderRow = order.orderRow.map(or => {
          if (savedRowInOrder ? or.orderRowId === orderRow.orderRowId : or.eventId == orderRow.eventId) {
            or = orderRow;
          }
          return or;
        });
      }

      if (orderRow?.eventId) {
        const index = order.orderRow.findIndex(or => or.eventId === orderRow.eventId && or.status !== orderRow.status && or.orderRowId);
        if (index !== -1) {
          order.orderRow[index].status = orderRow.status;
        }
      }

      if (orderRow.hotelGroupId && orderRow.hotelAllotmentPricingId) {
        if (orderRow.deleted) {
          order.orderRow = order.orderRow.filter(or => or.orderRowId !== orderRow.orderRowId);
        } else if (
          !order.orderRow.some(or => or.hotelGroupId === orderRow.hotelGroupId && or.hotelAllotmentPricingId === orderRow.hotelAllotmentPricingId) &&
          !orderRow.deleted
        ) {
          order.orderRow.push(orderRow);
        } else {
          const index = order.orderRow.findIndex(
            or =>
              (or.orderRowId === orderRow.orderRowId) &
              (or.hotelGroupId === orderRow.hotelGroupId) &
              (or.hotelAllotmentPricingId === orderRow.hotelAllotmentPricingId)
          );
          if (index !== -1) {
            order.orderRow[index] = orderRow;
          }
        }
      }

      if (!order.orderExtra) {
        order.orderExtra = [];
      }

      if (forceUncheck && displayYesNo && !order.orderExtra.find(oe => oe.name === 'EventRejected' && oe.value == orderRow.eventId)) {
        order.orderExtra.push({ name: 'EventRejected', value: orderRow.eventId, value2: '' });
      } else if (!forceUncheck && displayYesNo && order.orderExtra.find(oe => oe.name === 'EventRejected' && oe.value == orderRow.eventId)) {
        order.orderExtra = order.orderExtra.filter(oe => !(oe.name === 'EventRejected' && oe.value == orderRow.eventId));
      }

      if (updateSqlStateTimeout) {
        clearTimeout(updateSqlStateTimeout);
      }
      updateSqlStateTimeout = setTimeout(this.updateSqlState, 500);

      if (order.orderId !== this.orders[this.activePersonNo].orderId) {
        this.orders[this.orders.findIndex(o => o.orderId === order.orderId)] = order;
      } else {
        this.orders[this.activePersonNo] = order;
      }

      this.orders = [...this.orders];
    });
  };

  removeOrder = index => {
    var newOrderList = Array.from(this.orders);
    var newActivePersonNo = 0;

    if (this.orders.length > 1) {
      const orderToRemove = newOrderList[index];
      const indexToRemove = newOrderList.indexOf(orderToRemove);
      if (indexToRemove != -1) {
        if (indexToRemove <= this.activePersonNo) {
          newActivePersonNo = this.activePersonNo - 1;
        }
        newOrderList.splice(index, 1);
      }
    }
    this.activePersonNo = newActivePersonNo;
    this.orders = newOrderList;
  };

  setActiveOrder = (index, { skipSnapshot = false, preserveValidate = false } = {}) => {
    runInAction(() => {
      if (!skipSnapshot) {
        this.snapshotCurrentOrderValidation();
      }
      this.activePersonNo = index;
      if (!preserveValidate) {
        const targetState = this.orderValidationStates[this.orders[index]?.orderId];
        this.validate = targetState?.hasErrors ?? false;
      }
      this.checkConditionsOld();
      this.checkConditions();
    });
  };

  setActivePage = int => {
    runInAction(() => {
      // Capture the current page's validation state for the active order before leaving it,
      // so a multi-page walk-through accumulates per-page validation across all pages.
      this.snapshotCurrentOrderValidation();
      this.current.activePage = int;
      if (this.contentLayout?.activateGroupLeaderOnPage && this.contentLayout?.activateGroupLeaderOnPage == this.current?.activePage) {
        runInAction(() => {
          let leader = this.orders.find(x => x.leader);
          if (leader) {
            // The outgoing order's page was already snapshotted above, before activePage
            // changed. Snapshotting again here would run against the page being entered,
            // recording its untouched required fields as errors and flipping `validate`
            // on — showing validation errors the moment the page renders. The just-entered
            // page must stay unvalidated until the user tries to proceed, so skip both.
            this.setActiveOrder(this.orders.indexOf(leader), { skipSnapshot: true, preserveValidate: true });
          }
        });
      }
    });
  };

  setLeader = index => {
    var newOrderList = Array.from(this.orders);
    newOrderList = newOrderList.map((n, i) => {
      runInAction(() => {
        n.leader = false;
        if (i == index) n.leader = true;
      });
      return n;
    });
    this.orders = newOrderList;
  };

  newOrderId = () => {
    const nonCreatedOrders = this.orders?.filter(({ orderId }) => orderId < 0)?.map(({ orderId }) => orderId) ?? [];

    if (!nonCreatedOrders.length) return -1;

    const newOrderId = (Math.min(...nonCreatedOrders) ?? 0) - 1;
    return newOrderId;
  };

  createOrder = () => {
    this.snapshotCurrentOrderValidation();
    const newOrder = {};
    newOrder.sqlStateNo = this.sqlStateNo; // fix
    newOrder.contentLayoutId = this.contentLayout.contentLayoutId;
    newOrder.eventId = this.contentLayout.eventId;
    newOrder.languageCode = this.environment.lang;
    newOrder.orderId = this.newOrderId();

    if (this.orders.length > 0) {
      newOrder.leader = false;
      newOrder.orderId = this.newOrderId();
    } else {
      newOrder.leader = true;
    }

    const exhibitorId = getUrlParam('exi');

    if (exhibitorId && !isNaN(exhibitorId)) {
      newOrder.orderExtra = [
        {
          orderExtraId: 0,
          name: 'ExhibitorInvite',
          value: `${exhibitorId}`,
          value2: '',
        },
      ];
    }

    if (!newOrder.orderRow) {
      newOrder.orderRow = [];
    }

    if (!newOrder.orderRow.find(row => row.eventId === this.mainEvent.eventId)) {
      newOrder.orderRow.push({
        amount: this.mainEvent.amount,
        comment: '',
        discount: 0,
        eventId: this.mainEvent.eventId,
        orderId: newOrder.orderId,
        orderRowId: newOrder.orderRow.length * -1,
        quantity: 1,
        status: null,
        vatPercent: this.mainEvent.vatPercent,
        deleted: false,
      });
    }
    if (updateSqlStateTimeout) {
      clearTimeout(updateSqlStateTimeout);
    }

    updateSqlStateTimeout = setTimeout(this.updateSqlState, 500);
    if (this.orders.length > 0 && toJS(this.orders)[this.activePersonNo]) {
      this.checkForGroupTransferValues(this.orders[this.activePersonNo], newOrder);
    }

    this.orders.push(newOrder);
    this.configureValidation();

    this.activePersonNo = this.orders.length > 0 ? this.orders.length - 1 : 0;
    this.validate = false; // new order starts with no error display

    const personTypeId = getUrlParam('pti');
    if (personTypeId && !isNaN(personTypeId)) {
      newOrder.personTypes = [{ orderId: 0, personTypeId: parseInt(personTypeId) }];
    }
    this.checkConditionsOld();
    this.checkConditions();
  };

  addPersonType = personTypeId => {
    let changed = false;
    runInAction(() => {
      if (!this.orders[this.activePersonNo].personTypes) {
        this.orders[this.activePersonNo].personTypes = [];
      }
      if (!this.orders[this.activePersonNo].personTypes.find(pt => pt.personTypeId === personTypeId)) {
        this.orders[this.activePersonNo].personTypes.push({
          orderId: 0,
          personTypeId,
        });
        this.orders[this.activePersonNo] = {
          ...this.orders[this.activePersonNo],
        };
        this.orders = [...this.orders];
        changed = true;
      }
    });
    if (changed) this.refreshPersonTypeConditions(personTypeId);
  };

  removePersonType = personTypeId => {
    if (this.orders?.[this.activePersonNo]?.personTypes) {
      let changed = false;
      runInAction(() => {
        const before = this.orders[this.activePersonNo].personTypes.length;
        this.orders[this.activePersonNo].personTypes = this.orders[this.activePersonNo].personTypes.filter(
          pt => pt.personTypeId !== personTypeId && !pt.personTypeOrderId
        );
        this.orders = [...this.orders];
        changed = this.orders[this.activePersonNo].personTypes.length !== before;
      });
      if (changed) this.refreshPersonTypeConditions(personTypeId);
    }
  };

  // Persontyper kan både sättas av villkor (action 'persontype') och läsas av villkor
  // (källa 'PERSONTYPE.<id>'). När en persontyp ändras måste de moduler som lyssnar på
  // den räknas om. Flaggan hindrar att den omräkningen i sin tur triggar en ny runda.
  refreshPersonTypeConditions = personTypeId => {
    if (this.reevaluatingPersonTypes) return;
    if (!this.contentLayout?.contentLayoutModules) return;
    this.reevaluatingPersonTypes = true;
    try {
      this.checkConditions(`PERSONTYPE.${personTypeId}`);
    } finally {
      this.reevaluatingPersonTypes = false;
    }
  };

  setWebConfirmation = webConfirmationId => {
    if (this.orders[this.activePersonNo]) {
      runInAction(() => {
        this.orders[this.activePersonNo].webConfirmationId = webConfirmationId;
        this.orders[this.activePersonNo] = {
          ...this.orders[this.activePersonNo],
        };
        this.orders = [...this.orders];
      });
    }
  };

  setSmsConfirmation = smsConfirmationId => {
    if (this.orders[this.activePersonNo]) {
      runInAction(() => {
        this.orders[this.activePersonNo].smsConfirmationId = smsConfirmationId;
        this.orders[this.activePersonNo] = {
          ...this.orders[this.activePersonNo],
        };
        this.orders = [...this.orders];
      });
    }
  };

  setEmailConfirmation = comContentId => {
    if (this.orders[this.activePersonNo]) {
      runInAction(() => {
        this.orders[this.activePersonNo].emailConfirmationId = comContentId;
        this.orders[this.activePersonNo] = {
          ...this.orders[this.activePersonNo],
        };
        this.orders = [...this.orders];
      });
    }
  };

  setSecondaryEmailConfirmation = comContentId => {
    if (this.orders[this.activePersonNo]) {
      runInAction(() => {
        this.orders[this.activePersonNo].secondaryEmailConfirmationId = comContentId;
        this.orders[this.activePersonNo] = {
          ...this.orders[this.activePersonNo],
        };
        this.orders = [...this.orders];
      });
    }
  };
  checkForGroupTransferValues(previousOrder, newOrder) {
    if (this.questions.length > 0) {
      this.modules.map(m => {
        if (m.settings?.groupTransfer) {
          if (m.type === 'FIELD') {
            const field = this.fields.find(f => f.fieldId == m.settings.fieldId);
            if (previousOrder[_.camelCase(field.type)]?.[_.camelCase(field.name)]) {
              if (!newOrder[_.camelCase(field.type)]) {
                newOrder[[_.camelCase(field.type)]] = {};
              }
              newOrder[[_.camelCase(field.type)]][_.camelCase(field.name)] = previousOrder[_.camelCase(field.type)][_.camelCase(field.name)];
            }
          } else if (m.type === 'EVENT') {
            let row = previousOrder?.orderRow?.find(or => or.eventId == m.settings.eventId);
            if (row) {
              if (!newOrder.orderRow) {
                newOrder.orderRow = [];
              }
              newOrder.orderRow.push({
                ...row,
                orderId: newOrder.orderId,
                orderRowId: newOrder.orderRow.length * -1,
              });
            }
          } else if (m.type === 'QUESTION') {
            const answer = previousOrder?.answer?.find(a => a.questionId == m.settings.questionId);
            if (answer) {
              if (!newOrder.answer) {
                newOrder.answer = [];
              }
              newOrder.answer.push({
                answerId: 0,
                interactionKey: null,
                orderId: 0,
                questionId: m.settings.questionId,
                answerRow: answer.answerRow?.map(ar => {
                  return {
                    answerId: 0,
                    answerRowId: 0,
                    questionOptionId: ar.questionOptionId,
                    text: ar.text,
                    deleted: false,
                  };
                }),
              });
            }
          }
        }
      });
    }
  }

  updateEventLabels = (groupName, groupData = {}) => {
    runInAction(() => {
      this.eventLabels = { ...this.eventLabels, [groupName]: groupData };
    });
  };

  triggerValidation = (trigger = true) => {
    runInAction(() => {
      this.validate = trigger;
    });
  };

  snapshotCurrentOrderValidation = () => {
    const currentOrder = this.orders[this.activePersonNo];
    if (!currentOrder) return;

    const modules = this.contentLayout?.contentLayoutModules?.map(clm => clm.contentModule) ?? [];
    const lang = this.environment?.lang;
    const currentPage = this.current.activePage;

    const invalidFieldsThisPage = this.getFilteredValidations()
      .map(v => {
        const module = modules.find(m => m.contentModuleId == v.id && (m.settings?.validation || v.label));
        let fieldName = null;
        let errorMessage = null;

        if (!v.decorator && !v.id?.toString().includes('INVOICE_') && module) {
          if (module.type === 'FIELD') {
            const fieldData = this.fields?.find(f => f.fieldId === module.settings?.fieldId);
            fieldName = fieldData?.fieldLanguage?.find(fl => fl.languageCode === lang)?.label
              || fieldData?.fieldLanguage?.find(fl => fl.languageCode === 'eng')?.label
              || null;
          } else if (module.type === 'QUESTION') {
            const question = this.questions?.find(q => q.questionId === module.settings?.questionId);
            fieldName = question?.questionLanguage?.find(fl => fl.languageCode === lang)?.label
              || question?.questionLanguage?.find(fl => fl.languageCode === 'eng')?.label
              || null;
          } else if (module.type === 'EVENT') {
            fieldName = this.breakouts?.find(b => b.eventId === module.settings?.eventId)?.name || null;
          }
          if (v.reason && module.settings?.validation?.[v.reason]?.[lang]) {
            errorMessage = module.settings.validation[v.reason][lang];
          }
        }

        return { id: v.id, label: v.label, page: v.page, reason: v.reason, decorator: v.decorator, type: v.type, message: v.message, fieldName, errorMessage };
      });

    runInAction(() => {
      const existing = this.orderValidationStates[currentOrder.orderId];
      const pages = existing?.pages ? { ...existing.pages } : {};
      pages[currentPage] = { invalidFields: invalidFieldsThisPage };

      const aggregated = Object.values(pages).flatMap(p => p.invalidFields);
      this.orderValidationStates[currentOrder.orderId] = {
        pages,
        hasErrors: aggregated.length > 0,
        invalidFields: aggregated,
      };
    });
  };

  getRequiredValidationPages = () => {
    const pages = new Set();
    this.validations.forEach(v => {
      if (v.tracksRequired && typeof v.page === 'number' && v.page > 0) {
        pages.add(v.page);
      }
    });
    return pages;
  };

  buildUnvalidatedPageError = page => ({
    id: `UNVALIDATED_PAGE_${page}`,
    label: 'PageNotValidated',
    page,
    reason: 'unvalidated',
    decorator: null,
    type: null,
    message: null,
    fieldName: null,
    errorMessage: null,
  });

  register = callback => {
    this.snapshotCurrentOrderValidation();

    const requiredPages = this.getRequiredValidationPages();

    // Pre-loaded orders (positive orderId, persisted in DB) were validated when saved.
    // Session-created orders (negative orderId from newOrderId()) must walk the required
    // pages so Field components can populate validity for each page.
    const isSessionCreated = order => typeof order?.orderId === 'number' && order.orderId < 0;

    let isValid = this.orders.every(order => {
      const state = this.orderValidationStates[order.orderId];
      if (!state) {
        if (!isSessionCreated(order)) return true;
        runInAction(() => {
          const syntheticErrors = Array.from(requiredPages).map(p => this.buildUnvalidatedPageError(p));
          this.orderValidationStates[order.orderId] = {
            pages: {},
            hasErrors: true,
            invalidFields: syntheticErrors.length > 0 ? syntheticErrors : [this.buildUnvalidatedPageError(0)],
          };
        });
        return false;
      }
      if (isSessionCreated(order)) {
        const missingPages = [];
        requiredPages.forEach(page => {
          if (!state.pages?.[page]) missingPages.push(page);
        });
        if (missingPages.length > 0) {
          runInAction(() => {
            const syntheticErrors = missingPages.map(p => this.buildUnvalidatedPageError(p));
            const realErrors = (state.invalidFields || []).filter(f => f.reason !== 'unvalidated');
            this.orderValidationStates[order.orderId] = {
              ...state,
              pages: state.pages || {},
              hasErrors: true,
              invalidFields: [...realErrors, ...syntheticErrors],
            };
          });
          return false;
        }
      } else {
        // Pre-loaded order: drop any stale synthetic unvalidated entries from earlier registers.
        const realErrors = (state.invalidFields || []).filter(f => f.reason !== 'unvalidated');
        if (realErrors.length !== (state.invalidFields || []).length) {
          runInAction(() => {
            this.orderValidationStates[order.orderId] = {
              ...state,
              hasErrors: realErrors.length > 0,
              invalidFields: realErrors,
            };
          });
        }
      }
      return !this.orderValidationStates[order.orderId].hasErrors;
    });

    if (isValid) {
      if (this.current.payMethod == 'dibs' || this.current.payMethod === 'stripe')
        isValid = this.getFilteredValidations().filter(v => v.valid == false).length == 0;
      isValid = this.checkInvoiceValidation(isValid);
    }

    if (!isValid) {
      this.triggerValidation(true);
      this.openOrderCart = true;
      if (typeof callback === 'function') setTimeout(() => callback(false), 0);
      return;
    }

    if (isValid) {
      let orders = Array.from(this.orders);
      if (this.current.payMethod) {
        orders.map(o => {
          o.payMethod = this.current.payMethod;
          o.contentLayoutId = this.contentLayout.contentLayoutId;
          if (!o.webConfirmationId) o.webConfirmationId = this.contentLayout.webConfirmation;
          if (!o.emailConfirmationId) o.emailConfirmationId = this.contentLayout.emailConfirmation;
          if (!o.smsConfirmationId) o.smsConfirmationId = this.contentLayout.smsConfirmation;
          if (!o.secondaryEmailConfirmationId) o.secondaryEmailConfirmationId = this.contentLayout.secondaryEmailConfirmation;
        });
      } else {
        orders.map(o => {
          o.contentLayoutId = this.contentLayout.contentLayoutId;
          if (!o.webConfirmationId) o.webConfirmationId = this.contentLayout.webConfirmation;
          if (!o.emailConfirmationId) o.emailConfirmationId = this.contentLayout.emailConfirmation;
          if (!o.smsConfirmationId) o.smsConfirmationId = this.contentLayout.smsConfirmation;
          if (!o.secondaryEmailConfirmationId) o.secondaryEmailConfirmationId = this.contentLayout.secondaryEmailConfirmation;
        });
      }

      const dto = [];

      orders.forEach(o => {
        const order = {
          orderId: o.orderId ? o.orderId : null,
          eventIds: [],
          rows: [],
          exi: getInviteExhibitorId(o),
          personTypeIds: getInvitePersonTypeIds(o),
        };

        if (o.orderRow) {
          // The invoice fee skips the capacity check (the server adds it without one).
          const invoiceFeeEventId = this.invoiceFeeEventId;
          o.orderRow
            .filter(({ orderRowId, status, eventId }) => orderRowId < 0 && status !== 'Waitlist' && (!invoiceFeeEventId || eventId !== invoiceFeeEventId))
            .forEach(or => {
              order.eventIds.push(or.eventId);
              order.rows.push({ eventId: or.eventId, quantity: or.quantity || 1 });
            });
        }
        if (!order.discounts) {
          order.discounts = [];
        }
        if (order.eventIds.length > 0) {
          dto.push(order);
        }
      });

      const postAfterAbstracts = async (orders, callback) => {
        // Submit selected abstracts before form post
        if (this.selectedAbstractIds.length > 0) {
          // Warn before submitting, listing the abstracts that will be locked.
          // The dialog is rendered by AbstractManagement, so only ask when one is present.
          if (this.layoutHasAbstractManagementModule) {
            const selected = this.abstracts
              .filter(a => this.selectedAbstractIds.includes(a.abstractId))
              .map(a => ({ abstractId: a.abstractId, title: a.title }));
            const confirmed = await this.requestAbstractSubmitConfirmation(selected);
            if (!confirmed) {
              if (typeof callback === 'function') callback(false);
              return;
            }
          }
          try {
            await this.submitAbstracts(this.selectedAbstractIds);
          } catch (error) {
            console.error('Failed to submit abstracts during registration', error);
            this.globalAlert = 'RegisterFailed';
            if (typeof callback === 'function') callback(false);
            return;
          }
        } else if (
          this.layoutHasAbstractManagementModule &&
          this.layoutHasAbstractSubmitModule &&
          this.abstracts.some(a => a.status === 'Draft' || a.status === 'RevisionRequired')
        ) {
          // Submit module present but nothing selected: warn that the drafts will NOT
          // be submitted before completing the registration.
          const confirmed = await this.requestAbstractSubmitConfirmation([], 'none');
          if (!confirmed) {
            if (typeof callback === 'function') callback(false);
            return;
          }
        } else if (
          this.layoutHasAbstractManagementModule &&
          !this.layoutHasAbstractSubmitModule &&
          this.currentAbstract &&
          this.isCurrentAbstractEditable
        ) {
          // No AbstractSubmit module on the layout: registering submits the current
          // abstract directly, after the user confirms the warning dialog.
          const confirmed = await this.requestAbstractSubmitConfirmation([
            { abstractId: this.currentAbstract.abstractId, title: this.currentAbstract.title },
          ]);
          if (!confirmed) {
            if (typeof callback === 'function') callback(false);
            return;
          }
          try {
            await this.submitAbstracts([this.currentAbstract.abstractId]);
          } catch (error) {
            console.error('Failed to submit abstract during registration', error);
            this.globalAlert = 'RegisterFailed';
            if (typeof callback === 'function') callback(false);
            return;
          }
        }
        if (this.contentLayout.type === 'SURVEY') this.surveyPost(orders, callback);
        else this.formPost(orders, callback);
      };

      if (dto.length > 0) {
        axiosClient.post(`./attendee/statusrange/${this.sqlStateNo}`, dto).then(({ data }) => {
          if (data.available) {
            postAfterAbstracts(orders, callback);
          } else {
            alert('OrderRowFull');
          }
        });
      } else {
        postAfterAbstracts(orders, callback);
      }
    }
  };

  surveyPost = (orders, callback) => {
    axiosClient
      .post(`./cms/postSurvey/`, orders)
      .then(({ data }) => {
        let item = localStorage.getItem('formstates');
        if (item) {
          let states = JSON.parse(item);
          let prevState = states.find(s => s.formId === this.contentLayout.contentLayoutId && s.expires > Date.now());
          if (prevState) {
            states.splice(states.indexOf(prevState), 1);
            localStorage.setItem('formstates', JSON.stringify(states));
          }
        }
        if (localStorage.getItem('9983memberCache')) localStorage.removeItem('9983memberCache');
        // Registration completed — don't let the unsaved-abstract guard block the redirect.
        this.abstractDirty = false;
        window.location.href = window.location.origin + (!window.location.href.includes('localhost') ? '/cview' : '') + data.redirectUrl;
      })
      .catch(err => {
        if (typeof callback === 'function') {
          callback(false);
        }
        this.globalAlert = 'RegisterFailed';
      });
  };

  showQuestionModule = id => {
    let foundModule = true;
    let specificModule = this.contentLayout.contentLayoutModules.find(x => x.contentModule.type == 'QUESTION' && x.contentModule.settings.questionId == id);
    if (specificModule) {
      let isHidden = this.conditions[specificModule.contentLayoutModuleId]?.hidden;
      if (isHidden) {
        foundModule = false;
      }
    }
    return foundModule;
  };

  formPost = (orders, callback) => {
    orders = _.cloneDeep(orders);
    //exclude hidden required questions
    orders.forEach(order => {
      if (order && order?.person && typeof order.person === 'object') {
        order.person = Object.entries(order.person).reduce((acc, key) => {
          acc[key[0]] = key?.[1] && typeof key?.[1] === 'string' ? key?.[1]?.trim() : key?.[1];
          return acc;
        }, {});
      }

      if (order.answer && order.answer.length > 0) {
        // Exclude abstract-tagged question answers — those are handled by the abstract endpoints
        if (this.abstractTaggedQuestionIds.length > 0) {
          order.answer = order.answer.filter(a => !this.abstractTaggedQuestionIds.includes(a.questionId));
        }
        order.answer.forEach(question => {
          let include = this.showQuestionModule(question.questionId);
          if (!include) {
            order.answer = order.answer.filter(x => x.questionId != question.questionId);
          }
        });
      }
    });

    axiosClient
      .post(`./cms/post/`, orders)
      .then(({ data }) => {
        let item = localStorage.getItem('formstates');
        if (item) {
          let states = JSON.parse(item);
          let prevState = states.find(s => s.formId === this.contentLayout.contentLayoutId && s.expires > Date.now());
          if (prevState) {
            states.splice(states.indexOf(prevState), 1);
            localStorage.setItem('formstates', JSON.stringify(states));
          }
        }
        if (localStorage.getItem('9983memberCache')) localStorage.removeItem('9983memberCache');
        // Registration completed — don't let the unsaved-abstract guard block the redirect.
        this.abstractDirty = false;
        const searchParams = new URLSearchParams(window.location.search);
        const station = searchParams.get('station');
        window.location.href =
          window.location.origin + (!window.location.href.includes('localhost') ? '/cms' : '') + data.redirectUrl + (station ? '&station=' + station : '');
      })
      .catch(err => {
        if (typeof callback === 'function') {
          callback(false);
        }
        this.globalAlert = 'RegisterFailed';
      });
  };

  // Modules with conditions can live in the body, header or footer layout.
  allContentLayoutModules = () => {
    return [this.contentLayout, this.contentHeaderLayout, this.contentFooterLayout].flatMap(layout => layout?.contentLayoutModules ?? []);
  };

  checkConditions = sourceKey => {
    let targets = [];
    this.allContentLayoutModules().forEach(clm => {
      if (
        (Array.isArray(clm.conditions) && (!sourceKey || clm.conditions.some(c => c.sourceKey === sourceKey))) ||
        (Array.isArray(clm.conditions) && clm.conditions.some(c => c.sourceKey === 'DATE'))
      ) {
        targets.push(clm);
      }
    });
    if (Array.isArray(this.contentLayout.conditions) && (!sourceKey || this.contentLayout.conditions.some(c => c.sourceKey === sourceKey))) {
      targets.push(this.contentLayout);
    }
    targets.forEach(async target => {
      await this.resolveQuery(target);
    });
  };
  checkConditionsOld = contentLayoutModuleId => {
    let targets = [];
    this.allContentLayoutModules().map(clm => {
      if (
        Array.isArray(clm.conditions) &&
        ((!contentLayoutModuleId && clm.conditions.some(c => c.sourceId > 0)) ||
          clm.conditions.some(c => c.sourceId == contentLayoutModuleId) ||
          clm.contentLayoutModuleId == contentLayoutModuleId)
      ) {
        targets.push(clm);
      }
    });
    if (
      Array.isArray(this.contentLayout.conditions) &&
      (!contentLayoutModuleId || this.contentLayout.conditions.some(c => c.sourceId == contentLayoutModuleId))
    ) {
      targets.push(this.contentLayout);
    }
    if (this.environment.eventId === 9983) {
      targets.forEach(async target => {
        await this.customResolveQuery(target);
      });
    } else {
      targets.forEach(async target => {
        this.resolveQuery(target);
      });
    }
  };
  resolveQuery = async target => {
    //ALla conditions för det här target elementet
    //filtrerar alla conditons som inte är i grupp 0

    // let queryStr = "";
    let conditionLists = {};

    for (const action in conditionConstants.actions) {
      if (conditionConstants.actions.hasOwnProperty(action)) {
        conditionLists[conditionConstants.actions[action]] = target.conditions.filter(el => el.action == conditionConstants.actions[action]);
      }
    }
    for (const action in conditionLists) {
      if (conditionLists.hasOwnProperty(action)) {
        if (conditionLists[action] && conditionLists[action].length > 0) {
          const secondaryActions = [
            ...new Set(
              conditionLists[action].map(c => {
                return c.secondaryAction;
              })
            ),
          ];
          for (const secondaryAction of secondaryActions) {
            let queryStr = '';
            const conditionsToProcess = conditionLists[action]
              .filter(c => c.secondaryAction === secondaryAction)
              .sort((a, b) => {
                return a.sortOrder - b.sortOrder;
              });
            for (const c of conditionsToProcess) {
              if (c.prefix) queryStr += ' ' + c.prefix.replace('AND', '&&').replace('OR', '||') + ' ';

              if (c.sourceKey === 'DATE') {
                const date = new Date();

                const dateValue = c.value ? new Date(c.value) : null;

                if (c.operator === 'GREATER_THAN' && dateValue) {
                  queryStr += `(${date.getTime() > dateValue.getTime()})`;
                } else if (c.operator === 'LESS_THAN' && dateValue) {
                  queryStr += `(${date.getTime() < dateValue.getTime()})`;
                }
              } else {
                queryStr += c.sourceId ? this.resolveConditionOld(c) : await this.resolveCondition(c);
              }

              if (c.suffix) queryStr += ' ' + c.suffix.replace('AND', '&&').replace('OR', '||') + ' ';
            }
            try {
              let result = eval(queryStr);
              this.executeAction(action, secondaryAction, target.contentLayoutModuleId, result);
            } catch (err) {
              console.error(`Invalid Condition in {${target.contentModule?.type}} module: #${target.contentModule?.cssId}`, err);
            }
          }
        }
      }
    }
  };
  customResolveQuery = async target => {
    let conditionLists = {};

    for (const action in conditionConstants.actions) {
      if (conditionConstants.actions.hasOwnProperty(action)) {
        conditionLists[conditionConstants.actions[action]] = target.conditions.filter(el => el.action == conditionConstants.actions[action]);
      }
    }

    for (const action in conditionLists) {
      if (conditionLists.hasOwnProperty(action)) {
        if (conditionLists[action] && conditionLists[action].length > 0) {
          const secondaryActions = [
            ...new Set(
              conditionLists[action].map(c => {
                return c.secondaryAction;
              })
            ),
          ];
          for (const secondaryAction of secondaryActions) {
            let queryStr = '';
            const queryList = conditionLists[action]
              .filter(c => c.secondaryAction === secondaryAction)
              .sort((a, b) => {
                return a.sortOrder - b.sortOrder;
              });

            for (const c of queryList) {
              const source = this.extractElement(c.sourceId);
              const sourceValue = conditionNavigator.navigate(source, this.orders, this.activePersonNo);

              if (c.prefix) queryStr += ' ' + c.prefix.replace('AND', '&&').replace('OR', '||') + ' ';
              if (
                source?.contentModule.type === 'FIELD' &&
                source?.contentModule?.settings?.fieldType === 'OrgNo' &&
                c.operator === 'LIKE' &&
                sourceValue &&
                validateOrgno(sourceValue, 'SWE')
              ) {
                if (cache[sourceValue] !== null && cache[sourceValue] !== undefined) {
                  if (c.value === 'member_lookup_true') {
                    queryStr = cache[sourceValue] ? `('true' == 'true')` : `('false' == 'true')`;
                  }

                  if (c.value === 'member_lookup_false') {
                    queryStr = !cache[sourceValue] ? `('true' == 'true')` : `('false' == 'true')`;
                  }
                } else {
                  try {
                    const response = await basedAxios.get(`./utility/member/${sourceValue}`);
                    const isMember = response.data;

                    cache[sourceValue] = isMember;

                    if (c.value === 'member_lookup_true') {
                      queryStr = isMember ? `('true' == 'true')` : `('false' == 'true')`;
                    }

                    if (c.value === 'member_lookup_false') {
                      queryStr = !isMember ? `('true' == 'true')` : `('false' == 'true')`;
                    }
                  } catch (error) {
                    queryStr = `('false' == 'true')`;
                  }
                }
              } else {
                queryStr += this.resolveCondition(c);
              }
              if (c.suffix) queryStr += ' ' + c.suffix.replace('AND', '&&').replace('OR', '||') + ' ';
            }

            try {
              let result = eval(queryStr);
              this.executeAction(action, secondaryAction, target.contentLayoutModuleId, result);
            } catch (err) {
              console.error(`Invalid Condition in {${target.contentModule?.type}} module: #${target.contentModule?.cssId}`, err);
            }
          }
        }
      }
    }
  };
  resolveCondition = async condition => {
    const paramNames = condition.sourceKey.split('.');
    const sourceValue = conditionNavigator.navigate(condition.sourceKey, this.orders, this.activePersonNo, this.environment);
    if (paramNames[0] === 'EVENT' && condition.operator === 'FULL') {
      if (this.breakoutsFullbooked[paramNames[1]]) {
        return `('true' == 'true')`;
      } else {
        return `('false' == 'true')`;
      }
    } else if (paramNames[0] === 'EVENT' && condition.operator === 'NOT_FULL') {
      if (this.breakoutsFullbooked[paramNames[1]]) {
        return `('false' == 'true')`;
      } else {
        return `('true' == 'true')`;
      }
    } else if (paramNames[0] === 'EVENT' && condition.operator === 'VOUCHER_FULL') {
      if (this.voucherFullbooked[paramNames[1]]) {
        return `('true' == 'true')`;
      } else {
        return `('false' == 'true')`;
      }
    } else if (
      this.environment.eventId === 9983 &&
      paramNames[1] === 'OrgNo' &&
      condition.operator === 'LIKE' &&
      sourceValue &&
      validateOrgno(sourceValue, 'SWE')
    ) {
      const cache = localStorage.getItem('9983memberCache') ? JSON.parse(localStorage.getItem('9983memberCache')) : {};
      if (cache[sourceValue] !== null && cache[sourceValue] !== undefined) {
        return `('${cache[sourceValue]}' == '${condition.value === 'member_lookup_true'}')`;
      } else {
        try {
          const response = await basedAxios.get(`./utility/member/${sourceValue}`);
          const isMember = response.data;
          cache[sourceValue] = isMember;
          localStorage.setItem('9983memberCache', JSON.stringify(cache));
          return `('${cache[sourceValue]}' == '${condition.value === 'member_lookup_true'}')`;
        } catch (error) {
          return `('false' == 'true')`;
        }
      }
    } else if (Array.isArray(sourceValue) || condition.operator === conditionConstants.operators.LIKE) {
      return this.resolveCheckboxCondition(sourceValue, condition);
    } else {
      if (condition.operator.includes('GREATER') || condition.operator.includes('LESS')) {
        return `(${sourceValue?.length ?? 0} ${conditionTranslator.translateOperator(condition.operator)} ${condition.value})`;
      } else if (condition.operator.includes('ANY')) {
        return `(${sourceValue?.length ?? 0} > 0)`;
      } else {
        return `('${sourceValue}' ${conditionTranslator.translateOperator(condition.operator)} '${condition.value}')`;
      }
    }
  };
  resolveConditionOld = condition => {
    const source = this.extractElement(condition.sourceId);
    let conditionStr = '';

    if (source) {
      const sourceValue = conditionNavigator.navigateOld(source, this.orders, this.activePersonNo, this.environment);
      if (source.contentModule.type === 'EVENT' && condition.operator === 'FULL') {
        if (this.breakoutsFullbooked[source.contentModule.settings.eventId]) {
          conditionStr = `('true' == 'true')`;
        } else {
          conditionStr = `('false' == 'true')`;
        }
      } else if (source.contentModule.type === 'EVENT' && condition.operator === 'NOT_FULL') {
        if (this.breakoutsFullbooked[source.contentModule.settings.eventId]) {
          conditionStr = `('false' == 'true')`;
        } else {
          conditionStr = `('true' == 'true')`;
        }
      } else if (source.contentModule.type === 'EVENT' && condition.operator === 'VOUCHER_FULL') {
        if (this.voucherFullbooked[source.contentModule.settings.eventId]) {
          conditionStr = `('true' == 'true')`;
        } else {
          conditionStr = `('false' == 'true')`;
        }
      } else if (Array.isArray(sourceValue) || condition.operator === conditionConstants.operators.LIKE) {
        conditionStr = this.resolveCheckboxCondition(sourceValue, condition);
      } else {
        if ((condition.operator.includes('GREATER') || condition.operator.includes('LESS')) && sourceValue) {
          conditionStr = `(${sourceValue.length} ${conditionTranslator.translateOperator(condition.operator)} ${condition.value})`;
        } else if (condition.operator.includes('ANY')) {
          conditionStr = `(${sourceValue?.length ?? 0} > 0)`;
        } else {
          conditionStr = `('${sourceValue}' ${conditionTranslator.translateOperator(condition.operator)} '${condition.value}')`;
        }
      }
    }
    return conditionStr;
  };

  resolveCheckboxCondition = (sourceValue, condition) => {
    let cStr = '';
    if (condition.operator == conditionConstants.operators.ANY && sourceValue.length > 0) {
      cStr = '(true)';
    } else if (condition.operator == conditionConstants.operators.LIKE && sourceValue && sourceValue.toString().includes(condition.value)) {
      cStr = '(true)';
    } else if (condition.operator == conditionConstants.operators.EQUAL) {
      cStr = `(${sourceValue.some(sv => sv == condition.value)})`;
    } else if (condition.operator == conditionConstants.operators.NOT_EQUAL) {
      cStr = `(${!sourceValue.some(sv => sv == condition.value)})`;
    } else if (condition.operator == conditionConstants.operators.LESS_THAN_OR_EQUAL_TO) {
      cStr = `(${sourceValue.length <= condition.value})`;
    } else if (condition.operator == conditionConstants.operators.GREATER_THAN_OR_EQUAL_TO) {
      cStr = `(${sourceValue.length >= condition.value})`;
    } else {
      cStr = '(false)';
    }
    return cStr;
  };

  executeAction = (action, secondaryAction, id, value) => {
    if (action === 'persontype') {
      if (value) {
        this.addPersonType(parseInt(secondaryAction.replace('persontype', '').replace('pt', '')));
      } else {
        this.removePersonType(parseInt(secondaryAction.replace('persontype', '').replace('pt', '')));
      }
    } else if (action === 'webconf') {
      if (value) this.setWebConfirmation(parseInt(secondaryAction.replace('webconf', '')));
    } else if (action === 'emailconf') {
      if (value) this.setEmailConfirmation(parseInt(secondaryAction.replace('emailconf', '')));
    } else if (action === 'groupemailconf') {
      if (value) this.setSecondaryEmailConfirmation(parseInt(secondaryAction.replace('groupemailconf', '')));
    } else {
      runInAction(() => {
        if (!this.conditions[id])
          this.conditions[id] = {
            hidden: false,
            readonly: false,
            required: false,
          };
        switch (action) {
          case conditionConstants.actions.hidden:
            this.conditions[id].hidden = !value;
            break;
          case conditionConstants.actions.required:
            this.conditions[id].required = value;
            break;
          case conditionConstants.actions.readonly:
            this.conditions[id].readonly = value;
          case conditionConstants.actions.CustomError:
            break;
          default:
            break;
        }
      });
    }
  };

  extractElement = id => {
    if (isNaN(id) && id.startsWith('LANGUAGE')) {
      return { contentModule: { type: 'LANGUAGE' }, contentLayoutModuleId: 'LANGUAGE' };
    }

    if (isNaN(id) && id.startsWith('MEETING')) {
      return { contentModule: { type: 'MEETING' }, contentLayoutModuleId: 'MEETING' };
    }

    const source = this.allContentLayoutModules().find(clm => {
      return clm.contentLayoutModuleId === id;
    });
    return source;
  };

  setBreakoutFullbooked = (eventId, bookedStatus) => {
    runInAction(() => {
      this.breakoutsFullbooked[eventId] = bookedStatus;
    });
    this.checkConditionsOld();
    this.checkConditions();
  };

  setVoucherFullbooked = (eventId, bookedStatus) => {
    runInAction(() => {
      this.voucherFullbooked[eventId] = bookedStatus;
    });
    this.checkConditionsOld();
    this.checkConditions();
  };
}

// Bare store
export const timerStore = new ContentViewStore();
// Store as React context
export const useStore = () => timerStore;

// This makes the timer go
